import { LitElement, html, css, unsafeCSS } from 'lit';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { parseTimestamp } from '../utils/datetime.js';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import L from 'leaflet';
import base from '../styles/base.css?inline';
import { patternsSvg, layerTextures, soilLegend, createTextureDefs } from '../utils/boorprofiel.js';
import { getCompanyTextures, TEXTURES_EVENT } from '../utils/company-textures.js';
import { buildBoorprofielData } from '../utils/boorprofiel-data.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { BoringModel, maxPotsPerMixedSample, ringAreaSquareMeters } from '@mv/models';
import { Hand, MousePointerSquareDashed, Pentagon, MonitorUp, Pencil, StickyNote, X } from 'lucide';
import { lucideIcon } from '../icon.js';
import {
  SUBLOCATIONS_EVENT, SUBLOCATION_MAP_CSS, PolygonDrawer, drawSublocations, inSublocation,
  nextSublocationName, loadSublocations, saveNewSublocation, saveSublocation, removeSublocation, sublocationBounds,
} from '../utils/sublocations.js';
import { api } from '../api.js';
import { confirmDialog } from '../utils/confirm-dialog.js';
import './sublocation-editor.js';
import {
  renderProfielbalk, renderProfielKolommen, pasBzbIn, bzbKaartVan, renderBzbKaart, cmNaarM, PROFIELEN_CSS,
  profielKanaalNaam,
} from '../utils/lab-profielen.js';
import {
  potIdOf, overviewOf, visibleLayers, rejectReason, matchingPots, isFilterActive, EMPTY_FILTER, normalizeFilter,
  bzbGroep, BZB_GROEP_VOLGORDE, POSITION_LABELS,
  mixingWarnings, tooManyPotsWarning, maxPotsFor, MIN_INTENSITY_OPTIONS, potKind,
  packagingOf, potPassesPackaging, PACKAGING_LABELS, potInDepthRange, EMPTY_BORING_FILTER, boringPassesFilter,
  isWaterMatrix, watermonstersOf, isBottleOf,
} from '../utils/layer-overview.js';
import {
  MAPTILER_KEY, TILE_WORLD, TILE_BRT, MT_ATTR, PDOK_ATTR, KLIK_DREMPEL_PX, MARKER_CSS, kanaalNaam,
  koppelKader, erbijToets, KADER_UITLEG,
} from '../utils/lab-kaart.js';
import { renderFilterblok, FILTERBLOK_CSS } from '../utils/lab-filterblok.js';

/**
 * lab-mengmonsters — het tabblad Samenstelling: van veldgegevens naar mengmonsters, in één beeld.
 *
 * Bovenaan het filter op wat er in de grond zit, daaronder de kaart over de volle breedte en
 * daaronder de boorprofielen naast elkaar. Je klikt boringen aan op de kaart, hun profielen
 * verschijnen eronder, en daarin klik
 * je de potjes aan die naar het actieve mengmonster gaan. Alles in één beeld, omdat elke keuze
 * de volgende bepaalt: wie filtert op 'puin', wil op de kaart zien wáár dat zit en in het profiel
 * welk potje erbij hoort.
 *
 * Het filter verbergt niets, het dimt. Een laag die niet voldoet blijft in het profiel staan en
 * een boring zonder treffer blijft op de kaart: een filter dat dingen laat verdwijnen, laat je
 * twijfelen of je wel alles ziet.
 *
 * Deze component beslist niets over opslaan. Hij meldt wat de gebruiker koos
 * (`mengmonsters-changed`) en de pagina eromheen bewaart het. De assistent (Flip AI) bedient
 * hetzelfde filter en hetzelfde mandje via de methoden onder 'Voor de assistent'.
 */

/** Periodes van het filter. `uren` telt terug vanaf nu. */
const PERIODES = [
  { id: '24u', naam: '24 uur', uren: 24 },
  { id: 'week', naam: 'Deze week', uren: 24 * 7 },
  { id: 'alles', naam: 'Alles', uren: Infinity },
  // Eigen begin- en einddatum (_periodeVan / _periodeTot, 'JJJJ-MM-DD', beide inclusief).
  { id: 'aangepast', naam: 'Aangepast', uren: null },
];
const isDatum = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);



/**
 * Schaal van de profielen: een laag is zijn dikte × 60 px per meter hoog, maar nooit lager dan
 * één regel tekst, en hoog genoeg voor zijn potjes (POTJE_PX per stuk) zodat je ze kunt aanklikken.
 */
const PX_PER_M = 60;
/**
 * Schalen voor in- en uitzoomen bij de profielen. De gebruiker ziet een percentage, geen px/m:
 * 200 px/m is 100%, zodat elke stap een rond getal is (20%, 25%, … 250%).
 */
const PROFIEL_SCHALEN = [40, 50, 60, 80, 100, 150, 200, 250, 300, 400, 500];
const PX_PER_M_100 = 200;
const schaalProcent = (pxPerM) => Math.round((pxPerM / PX_PER_M_100) * 100);
/** De dichtstbijzijnde schaal uit de lijst, voor een bewaarde schaal uit een oudere versie. */
const dichtstbijzijndeSchaal = (px) => PROFIEL_SCHALEN.reduce((a, b) => (Math.abs(b - px) < Math.abs(a - px) ? b : a));

/**
 * Breedte van een profielkolom en van de balk met de potjes bij een schaal: tot 110 px/m de
 * gewone maat, daarboven groeien ze mee, zodat een groot profiel ook breed genoeg is om de
 * potjes en de beschrijving te lezen.
 */
function kolomMaten(pxPerM) {
  const extra = Math.max(0, pxPerM - 110);
  return { kolom: Math.round(300 + extra * 0.8), balk: Math.round(78 + extra * 0.12) };
}
const MIN_LAAG_PX = 22;
const POTJE_PX = 20;

/**
 * Eén diepte-as voor alle kolommen: dezelfde diepte ligt in elke kolom op dezelfde hoogte.
 *
 * De as is opgeknipt op elke laaggrens van elke boring. Elk stuk begint op zijn dikte × PX_PER_M.
 * Heeft een laag meer nodig dan zijn stukken samen (potjes, tekst, MIN_LAAG_PX), dan worden
 * díe stukken naar verhouding opgerekt -- en omdat een stuk voor alle kolommen geldt, rekt het
 * dieptetraject overal mee. Oprekken maakt een stuk alleen groter, dus een laag die al paste
 * blijft passen: één ronde over de lagen is genoeg.
 *
 * Zet per laag `hoogte` (px) en `gapBefore` (px leeg erboven, voor een gat tussen twee lagen of
 * een eerste laag die niet op 0 begint).
 *
 * `k.offset` (cm, standaard 0) schuift een kolom omlaag op de as: op NAP begint een boring met
 * een lager maaiveld lager. Geeft de as zelf terug: per grens (in cm op de as) zijn hoogte in px,
 * voor de Y-as links van de profielen.
 */
function applySharedDepthAxis(columns, pxPerM = PX_PER_M) {
  for (const k of columns) {
    const off = k.offset || 0;
    for (const l of k.lagen) { l.asVan = l.van + off; l.asTot = l.tot + off; }
  }
  const layers = columns.flatMap((k) => k.lagen);
  const bounds = [...new Set([0, ...layers.flatMap((l) => [l.asVan, l.asTot])])].sort((a, b) => a - b);
  const index = new Map(bounds.map((d, i) => [d, i]));
  const seg = bounds.slice(1).map((d, i) => ((d - bounds[i]) / 100) * pxPerM);

  for (const l of layers) {
    const from = index.get(l.asVan), to = index.get(l.asTot);
    if (!(to > from)) continue;
    let sum = 0;
    for (let i = from; i < to; i++) sum += seg[i];
    if (sum >= l.minHeight) continue;
    const factor = l.minHeight / sum;
    for (let i = from; i < to; i++) seg[i] *= factor;
  }

  // Afronden op de as zelf en niet per laag: anders loopt een kolom met veel dunne lagen een
  // paar pixels uit de pas met een kolom met één dikke.
  const y = [0];
  for (const h of seg) y.push(y.at(-1) + h);
  const at = (d) => Math.round(y[index.get(d)]);

  for (const k of columns) {
    let bottom = 0;
    for (const l of k.lagen) {
      // Een laag zonder dikte heeft geen plek op de as; hij krijgt zijn minimum, en de kolom
      // loopt daaronder een stukje achter. Komt in de praktijk niet voor.
      l.hoogte = l.tot > l.van ? at(l.asTot) - at(l.asVan) : l.minHeight;
      l.gapBefore = Math.max(0, at(l.asVan) - bottom);
      bottom = Math.max(bottom, at(l.asTot));
    }
  }
  return bounds.map((d) => ({ d, y: at(d) }));
}

/** Namespace voor de patroon-id's, zodat ze niet botsen met een ander boorprofiel. */
const PAT = 'mm';

/**
 * De patronen van Figuur 1 staan één keer in de shadow-DOM; elke tekening verwijst ernaar.
 *
 * Let op bij het uitbreiden: alles wat bínnen een <svg> staat en uit een genest sjabloon komt,
 * moet met lit's `svg`-tag en niet met `html`. Een genest `html`-sjabloon met <g> als wortel
 * wordt als HTML ontleed, en de laag blijft dan onzichtbaar.
 */
const PATROON_DEFS = html`<svg class="defs" width="0" height="0" aria-hidden="true">${unsafeSVG(patternsSvg(PAT))}</svg>`;

/**
 * Kleur van een BZB-chip naar soort. Puin en asbest zijn wat je niet wilt mengen, geur wijst
 * op vluchtige verontreiniging: die twee moet je in één oogopslag uit elkaar houden.
 */
const BZB_STIJL = {
  puin: { bg: '#fdf7f6', rand: '#f0d6d3', kleur: '#a33b31' },
  geur: { bg: '#fffaf0', rand: '#f0e2c4', kleur: '#8c6512' },
  overig: { bg: '#f6f9fd', rand: '#d6e2f0', kleur: '#1c5fa8' },
};
const bzbStijl = (naam) => (/puin|glas|beton|asbest|baksteen/i.test(naam) ? BZB_STIJL.puin
  : /geur|olie|zwavel|kolen|slak|asfalt/i.test(naam) ? BZB_STIJL.geur : BZB_STIJL.overig);

const ICOON_CHEVRON = html`<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
  stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"></path></svg>`;

/**
 * Staat deze boring ergens op de kaart?
 *
 * `Number(null)` en `Number('')` zijn allebei 0, en 0 is een geldig getal. Een boring zonder
 * coördinaat belandde daardoor op 0,0 -- in de Golf van Guinee -- en trok de kaart mee bij het
 * inpassen. Een echte plek is dus: twee getallen, niet allebei nul, binnen het bereik van graden.
 */
function heeftPlek(b) {
  const leeg = (v) => v === null || v === undefined || v === '';
  if (leeg(b?.x) || leeg(b?.y)) return false;
  const x = Number(b.x), y = Number(b.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return false;
  if (x === 0 && y === 0) return false;
  return Math.abs(y) <= 90 && Math.abs(x) <= 180;
}

/**
 * Elk mengmonster draagt een vaste sleutel naast zijn naam. Voor een bewaard mengmonster is dat
 * zijn id in de backend; een nieuw krijgt een tijdelijke, die de pagina na het aanmaken aan het
 * echte id koppelt. Zo blijft een mengmonster hetzelfde mengmonster als je het hernoemt, en
 * erft een nieuw MM02 niets van een weggegooid MM02.
 */
const nieuweSleutel = () => `nieuw-${Math.random().toString(36).slice(2, 10)}`;
const leegMengmonster = (code) => ({ sleutel: nieuweSleutel(), code, notitie: '', potjes: [] });

/**
 * Welke panelen (filter, kaart) open staan, hoe groot ze zijn en de schaal van de profielen;
 * per browser onthouden.
 */
const PANELEN_KEY = 'mv-lab-samenstelling-panelen';
function leesPanelen() {
  try { return JSON.parse(localStorage.getItem(PANELEN_KEY) || '{}') || {}; } catch (_) { return {}; }
}

const meervoud = (n, een, veel) => `${n} ${n === 1 ? een : veel}`;
/** Een naam zoals we hem bewaren: zonder spaties eromheen en zonder dubbele spaties. */
const schoneNaam = (naam) => String(naam || '').trim().replace(/\s+/g, ' ');
const opNaam = (a, b) => a.localeCompare(b, 'nl');

export class LabMengmonsters extends LitElement {
  static properties = {
    /** Het kaartje met alle BZB's van één laag ('+4' aangeklikt), of null. */
    _bzbKaart: { state: true },
    /** Boringen van het project, met lagen, monsters, x/y en maaiveldhoogte. */
    borings: { type: Array },
    /**
     * De mengmonsters die al bewaard zijn, in dezelfde vorm als wat dit onderdeel meldt.
     * Zonder deze zou je na het herladen naar een leeg mandje kijken terwijl ze wel bestaan.
     */
    bestaande: { type: Array },
    /** Verstuurde opdracht: kijken mag, wijzigen niet meer. */
    vergrendeld: { type: Boolean },
    /**
     * Staat het tabblad in beeld? De pagina houdt dit onderdeel ook op de andere tabbladen in
     * de DOM (de assistent werkt overal), maar Leaflet kan een verborgen kaart niet meten.
     */
    zichtbaar: { type: Boolean },
    /** Monstertype van de opdracht ('grond', 'asfalt', ...); asfalt gaat in een eigen opdracht. */
    monstertype: { type: String },
    /** Project van de opdracht: daar horen de deellocaties bij. */
    projectId: { type: String },
    _sublocations: { state: true },
    _dlMode: { state: true },      // null | 'drawing' | 'naming', zie utils/sublocations.js
    _dlPoints: { state: true },
    _dlName: { state: true },
    _dlError: { state: true },
    _dlBusy: { state: true },
    _filterOpen: { state: true },
    _schaal: { state: true },        // px per meter van de profielen
    _asModus: { state: true },       // Y-as van de profielen: 'maaiveld' (standaard) of 'nap'
    _kaartOpen: { state: true },
    _venster: { state: true },       // staat de kaart in een apart venster (tweede scherm)?
    _vensterFout: { state: true },
    _periode: { state: true },
    _periodeVan: { state: true },
    _periodeTot: { state: true },
    _selectie: { state: true },   // Set van boring-id's
    _filter: { state: true },     // zie EMPTY_FILTER in layer-overview.js
    _boringFilter: { state: true },  // welke boringen meedoen; zie EMPTY_BORING_FILTER
    _historie: { state: true },   // vorige filters, voor 'vorige filter' van de assistent
    _mengmonsters: { state: true },  // [{ code, notitie, potjes: [{ id, naam, boringNaam, van, tot, matrix }] }]
    _actief: { state: true },        // code van het mengmonster dat je nu vult
    _kaartModus: { state: true },    // 'selecteren' (kader trekken) of 'verplaatsen'
    _hernoemt: { state: true },      // code van het mengmonster waarvan de naam openstaat
    _hernoemFout: { state: true },
    _nieuwNaam: { state: true },     // naam van het analysemonster dat je aanmaakt (null = niet bezig)
    _nieuwFout: { state: true },
    _dlBeheer: { state: true },      // deellocaties hernoemen en verwijderen
    _dlHernoemt: { state: true },    // id van de deellocatie waarvan de naam openstaat
    _dlBeheerFout: { state: true },
    _dlBewerkt: { state: true },
    _notitieVoor: { state: true },   // code van het mengmonster waarvan de notitie openstaat
    _notitieTekst: { state: true },
    _pvVenster: { state: true },     // de boorprofielen staan in een apart venster
    _pvFout: { state: true },     // id van de deellocatie waarvan het bewerkvenster openstaat
    _legenda: { state: true },
  };

  constructor() {
    super();
    this.borings = [];
    this.zichtbaar = true;
    this.monstertype = 'grond';
    // 'Alles' als begin: een filter dat bij het openen al gegevens verbergt, laat je zoeken
    // naar boringen waarvan je zeker weet dat ze er zijn.
    this._periode = 'alles';
    this._periodeVan = null;
    this._periodeTot = null;
    this._selectie = new Set();
    this._filter = { ...EMPTY_FILTER };
    this._boringFilter = { ...EMPTY_BORING_FILTER };
    this._historie = [];
    this._mengmonsters = [];
    this._actief = null;
    this._legenda = true;
    this._overzichtCache = new WeakMap();
    this._markers = new Map();
    this._gemarkeerd = null;
    // Selecteren is de stand waarin je begint: daarvoor kom je op dit scherm.
    this._kaartModus = 'selecteren';
    this._eigenMelding = false;
    this._tipBeweeg = this._tipBeweeg.bind(this);
    this._tipWeg = this._tipWeg.bind(this);
    this._tipTimer = null;
    this._tipDoel = null;
    this._tipAan = false;
    this._sublocations = [];
    this._dlMode = null;
    this._dlPoints = 0;
    this._dlName = '';
    this._dlError = '';
    this._dlBusy = false;
    this._onSublocations = (e) => {
      if (e.detail?.projectId === this.projectId) this._sublocations = e.detail.sublocations;
    };
    this._onKey = (e) => {
      if (e.key === 'Escape' && this._bzbKaart) { this._bzbKaart = null; return; }
      if (e.key === 'Escape' && this._dlMode) this._cancelDrawing();
    };
    // Ingeklapt of open onthouden we per gebruiker: wie de kaart dichtklapt om ruimte voor de
    // profielen te maken, wil dat bij de volgende opdracht ook.
    const panelen = leesPanelen();
    this._filterOpen = panelen.filter !== false;
    this._kaartOpen = panelen.kaart !== false;
    // Maten van de vlakken in px, zoals je ze zelf hebt gesleept; zonder maat de standaard.
    this._maten = { ...(panelen.maten || {}) };
    this._schaal = Number.isFinite(panelen.schaal) ? dichtstbijzijndeSchaal(panelen.schaal) : PX_PER_M;
    this._asModus = panelen.as === 'nap' ? 'nap' : 'maaiveld';
  }

  _bewaarPanelen() {
    try {
      localStorage.setItem(PANELEN_KEY, JSON.stringify({
        filter: this._filterOpen, kaart: this._kaartOpen, maten: this._maten, schaal: this._schaal, as: this._asModus,
      }));
    } catch (_) { /* privévenster of geblokkeerde opslag: dan alleen voor nu */ }
  }

  _zetPaneel(naam, open) {
    if (naam === 'filter') this._filterOpen = open; else this._kaartOpen = open;
    this._bewaarPanelen();
  }

  _zetAsModus(modus) {
    if (modus === this._asModus) return;
    this._asModus = modus;
    this._bewaarPanelen();
  }

  _zetSchaal(stap) {
    const i = PROFIEL_SCHALEN.indexOf(this._schaal);
    const volgende = PROFIEL_SCHALEN[Math.max(0, Math.min(PROFIEL_SCHALEN.length - 1, (i < 0 ? 1 : i) + stap))];
    if (volgende === this._schaal) return;
    this._schaal = volgende;
    this._bewaarPanelen();
  }

  // ── Verstelbare vlakken ─────────────────────────────────────────────────
  //
  // Iedereen deelt het scherm anders in (een laptop, een extra breed scherm), dus de kaart en
  // de profielen zijn met een greep groter en kleiner te slepen. De maat komt als CSS-variabele
  // op het onderdeel; dubbelklikken op een greep zet die maat terug op de standaard.

  static MATEN = {
    kaartHoogte: { css: '--kaart-hoogte', as: 'y', min: 180 },
    profielHoogte: { css: '--profiel-hoogte', as: 'y', min: 220 },
    kaartBreedte: { css: '--kaart-breedte', as: 'x', min: 320 },
  };

  _pasMatenToe() {
    for (const [naam, { css }] of Object.entries(LabMengmonsters.MATEN)) {
      const v = this._maten[naam];
      if (Number.isFinite(v)) this.style.setProperty(css, `${v}px`); else this.style.removeProperty(css);
    }
  }

  /** Slepen aan een greep: de maat volgt de muis, bij loslaten wordt hij onthouden. */
  _sleep(e, naam, element) {
    if (e.button !== 0) return;
    const doel = this.renderRoot.querySelector(element);
    if (!doel) return;
    e.preventDefault();
    const { as, min } = LabMengmonsters.MATEN[naam];
    const greep = e.currentTarget;
    greep.setPointerCapture(e.pointerId);
    greep.classList.add('sleept');
    const begin = as === 'x' ? doel.getBoundingClientRect().width : doel.getBoundingClientRect().height;
    const start = as === 'x' ? e.clientX : e.clientY;
    // De kaart mag niet zo breed dat er voor de profielen niets overblijft.
    const max = as === 'x'
      ? (this.renderRoot.querySelector('.werkvlak')?.getBoundingClientRect().width || 2000) - 360
      : Math.max(min, window.innerHeight * 2);
    const beweeg = (ev) => {
      const v = begin + ((as === 'x' ? ev.clientX : ev.clientY) - start);
      this._maten = { ...this._maten, [naam]: Math.round(Math.max(min, Math.min(max, v))) };
      this._pasMatenToe();
    };
    const los = () => {
      greep.removeEventListener('pointermove', beweeg);
      greep.removeEventListener('pointerup', los);
      greep.removeEventListener('pointercancel', los);
      greep.classList.remove('sleept');
      this._bewaarPanelen();
      this._kaart?.invalidateSize();
    };
    greep.addEventListener('pointermove', beweeg);
    greep.addEventListener('pointerup', los);
    greep.addEventListener('pointercancel', los);
  }

  _herstelMaat(naam) {
    const { [naam]: _weg, ...rest } = this._maten;
    this._maten = rest;
    this._pasMatenToe();
    this._bewaarPanelen();
    this._kaart?.invalidateSize();
  }

  _greep(naam, element, richting, uitleg) {
    return html`
      <div class="greep ${richting}" role="separator" aria-orientation=${richting === 'breedte' ? 'vertical' : 'horizontal'}
           title=${`${uitleg} · dubbelklik zet hem terug`}
           @pointerdown=${(e) => this._sleep(e, naam, element)} @dblclick=${() => this._herstelMaat(naam)}></div>`;
  }

  /** Het pijltje van de legenda, voor alles wat in- en uitklapt. */
  _inklapknop(open, label, onToggle, extra = '') {
    return html`
      <button class="legendaknop inklap" aria-expanded=${open} @click=${onToggle}>
        <svg class=${open ? 'pijl' : 'pijl dicht'} width="11" height="11" viewBox="0 0 24 24"
             fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round">
          <path d="m6 9 6 6 6-6"></path>
        </svg><span class="mv-caps">${label}</span>${extra}
      </button>`;
  }

  connectedCallback() {
    super.connectedCallback();
    this._pasMatenToe();
    // Opnieuw tekenen zodra de texturen van het bedrijf binnen zijn of wijzigen.
    this._onTextures = () => this.requestUpdate();
    window.addEventListener(TEXTURES_EVENT, this._onTextures);
    window.addEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.addEventListener('keydown', this._onKey);
  }

  disconnectedCallback() {
    window.removeEventListener(TEXTURES_EVENT, this._onTextures);
    window.removeEventListener(SUBLOCATIONS_EVENT, this._onSublocations);
    window.removeEventListener('keydown', this._onKey);
    this._drawer?.cancel();
    this._ontkoppelKader?.();
    this._kaartRO?.disconnect();
    this._bzbRO?.disconnect();
    this._bzbRO = null;
    // Weg van de labopdracht: het kaartvenster heeft dan niemand meer om mee te praten.
    this._sluitVenster();
    this._sluitProfielVenster();
    this._tipWeg();
    this._kaart?.remove();
    this._kaart = null;
    super.disconnectedCallback();
  }

  willUpdate(changed) {
    // Alleen overnemen wat van buiten komt, niet wat wij zelf net gemeld hebben: anders
    // overschrijft een antwoord van de server je volgende klik.
    if (!changed.has('bestaande') || !Array.isArray(this.bestaande)) return;
    if (this._eigenMelding) { this._eigenMelding = false; return; }
    this._mengmonsters = this.bestaande.map((m) => ({
      sleutel: m.sleutel || nieuweSleutel(), code: m.code, notitie: m.notitie || '', potjes: [...(m.potjes || [])],
    }));
    if (!this._mengmonsters.some((m) => m.code === this._actief)) {
      this._actief = this._mengmonsters.at(-1)?.code ?? null;
      this._meldActief();
    }
    /*
     * De boringen waar de potjes vandaan komen meteen selecteren.
     *
     * Zonder dit kwam je terug op een lege kaart: de mengmonsters stonden er wel, maar de
     * boorprofielen niet, en dan kun je er ook geen potje meer aan toevoegen. Alleen als je
     * zelf nog niets hebt gekozen.
     */
    if (!this._selectie.size) {
      const bekend = new Set(this.inPeriode.map((b) => b.id));
      const uitPotjes = this._mengmonsters
        .flatMap((m) => m.potjes.map((p) => p.boringId))
        .filter((id) => id && bekend.has(id));
      if (uitPotjes.length) this._selectie = new Set(uitPotjes);
    }
  }

  // ── Afgeleide gegevens ──────────────────────────────────────────────────

  /**
   * Boringen binnen de gekozen periode die door het boringfilter komen (met monsters, diep
   * genoeg). Alles wat hierbuiten valt bestaat voor dit scherm niet.
   */
  get inPeriode() {
    const bf = this._boringFilter;
    // Grondwater: 'met monsters' betekent hier met watermonsters; de grondlagen doen niet mee.
    if (this._waterModus) {
      return this._binnenPeriode().filter((b) => !bf.withSamplesOnly || (b.watermonsters || []).length);
    }
    return this._binnenPeriode().filter((b) => boringPassesFilter(this._overzicht(b), bf));
  }

  /**
   * Een labopdracht met monstertype Grondwater. Dan stel je geen mengmonsters uit grondpotjes
   * samen, maar kies je watermonsters: één watermonster is één analysemonster, met zijn flessen.
   */
  get _waterModus() { return this.monstertype === 'grondwater'; }

  _binnenPeriode() {
    const periode = PERIODES.find((p) => p.id === this._periode) || PERIODES[2];
    if (periode.id === 'aangepast') {
      // Lokale middernacht: 'tot en met 30-10' is tot het eind van die dag waar de gebruiker zit.
      const begin = this._periodeVan ? new Date(`${this._periodeVan}T00:00:00`).getTime() : -Infinity;
      const eind = this._periodeTot ? new Date(`${this._periodeTot}T23:59:59.999`).getTime() : Infinity;
      return (this.borings || []).filter((b) => {
        if (b.deletedAt) return false;
        const t = parseTimestamp(b.createdAt)?.getTime();
        return Number.isFinite(t) ? t >= begin && t <= eind : false;
      });
    }
    if (!Number.isFinite(periode.uren)) return (this.borings || []).filter((b) => !b.deletedAt);
    const grens = Date.now() - periode.uren * 3600_000;
    return (this.borings || []).filter((b) => {
      if (b.deletedAt) return false;
      // Via parseTimestamp: zonder de 'Z' las Date.parse een kale tijd uit de database als
      // lokale tijd, en dan hing 'de laatste 24 uur' af van de tijdzone van de kijker.
      const t = parseTimestamp(b.createdAt)?.getTime();
      return Number.isFinite(t) ? t >= grens : false;
    });
  }

  /** De gekozen boringen, in de volgorde van het project. Kies je er één in het filter, dan alleen die. */
  get gekozen() {
    const alleen = this._filter.boringIds;
    return this.inPeriode.filter((b) => this._selectie.has(b.id) && (!alleen.length || alleen.includes(b.id)));
  }

  // ── Het filter ──────────────────────────────────────────────────────────

  /** De lagen en potjes van een boring zoals het filter ze ziet; één keer per boring uitgerekend. */
  _overzicht(boring) {
    let o = this._overzichtCache.get(boring);
    if (!o) { o = overviewOf(boring); this._overzichtCache.set(boring, o); }
    // De deellocatie bij elk potje, voor de mengregels: niet over een deellocatie heen mengen,
    // en het maximum aantal potten volgt de strategie (zie maxPotsFor). Niet in de cache: de
    // deellocaties komen later binnen dan de boringen en kunnen veranderen.
    const dl = this._deellocatieVan(boring);
    for (const p of o.pots) p.deellocatie = dl;
    return o;
  }

  /**
   * De deellocatie van een boring met zijn strategie, of null. Ligt de boring in meer
   * deellocaties, dan telt de kleinste: dat is de meest specifieke, net als bij het monsterplan
   * (de kleinere deellocatie heeft het overlappende deel).
   */
  _deellocatieVan(boring) {
    const ids = boring?.sublocationIds || [];
    if (!ids.length || !this._sublocations?.length) return null;
    const kandidaten = this._sublocations.filter((s) => ids.includes(s.id));
    if (!kandidaten.length) return null;
    const opp = (s) => ((s.geometry || []).length >= 3 ? ringAreaSquareMeters(s.geometry) : Infinity);
    const s = kandidaten.sort((a, b) => opp(a) - opp(b))[0];
    return { id: s.id, naam: s.name, strategie: s.strategyCode || null, max: maxPotsPerMixedSample(s.strategyCode) };
  }

  /**
   * Voldoet laag `i` van deze boring aan het filter?
   *
   * 'Alleen bemonsterd' telt hier wel mee: een laag zonder potje kun je niet mengen, dus die
   * staat gedimd. Het telt níét als actief filter (zie isFilterActive) -- het is de stand
   * waarin je begint, en dan hoort er geen 'Wis filters' te staan.
   */
  _laagPast(boring, i) {
    const l = this._overzicht(boring).layers[i];
    if (!l) return true;
    const f = this._filter;
    if (f.sampledOnly && !l.pots.length) return false;
    if (f.boringIds.length && !f.boringIds.includes(boring.id)) return false;
    return !isFilterActive(f) || !rejectReason(l, f);
  }

  /** Heeft deze boring minstens één laag die voldoet? Zo niet, dan staat hij gedimd op de kaart. */
  _boringPast(boring) {
    if (this._waterModus) return (boring.watermonsters || []).length > 0;
    const f = this._filter;
    if (!isFilterActive(f)) return true;
    if (f.boringIds.length && !f.boringIds.includes(boring.id)) return false;
    return visibleLayers(this._overzicht(boring), f).some((l) => !rejectReason(l, f));
  }

  _zetFilter(next, { historie = true } = {}) {
    if (historie) this._historie = [...this._historie, this._filter].slice(-20);
    // Kies je in het filter één boring, dan wil je zijn profiel zien: die komt in de selectie.
    const nieuw = next.boringIds.filter((id) => !this._filter.boringIds.includes(id));
    if (nieuw.length) this._selectie = new Set([...this._selectie, ...nieuw]);
    // Flip en de woordherkenning geven grondwater en waarneming als één waarde; de keuzelijsten
    // werken met lijsten en een modus per filter.
    this._filter = normalizeFilter(next);
  }

  _patch(patch) { this._zetFilter({ ...this._filter, ...patch }); }

  _wisFilters() {
    this._zetFilter({ ...EMPTY_FILTER, sampledOnly: this._filter.sampledOnly });
  }

  /**
   * Een bestanddeel aan of uit. Nog een keer klikken zet terug wat je net deed; het wordt geen
   * 'zonder', want dan verdwijnen er lagen terwijl je alleen je keuze wilde ongedaan maken.
   * 'Zonder' kan de assistent zetten; dan haalt een klik die uitsluiting weer weg.
   */
  _wisselBzb(type) {
    const f = this._filter;
    if (f.include.includes(type)) this._patch({ include: f.include.filter((x) => x !== type) });
    else if (f.exclude.includes(type)) this._patch({ exclude: f.exclude.filter((x) => x !== type) });
    else this._patch({ include: [...f.include, type] });
  }

  // ── Voor de assistent ───────────────────────────────────────────────────
  //
  // Flip zweeft over alle tabbladen en bedient dit filter en dit mandje. Hij krijgt geen eigen
  // kopie: wat hij doet, zie je hier terug en kun je met dezelfde knoppen aanpassen.

  /** De boringen binnen de periode, teruggebracht tot lagen en potjes. */
  get overzichten() {
    return this.inPeriode.map((b) => this._overzicht(b));
  }

  get filter() { return this._filter; }
  get mengmonsters() { return this._mengmonsters; }
  get actief() { return this._actief; }
  /** Het hoogste MMnn-nummer dat er nu staat; de assistent nummert zijn voorstel daarachter. */
  get laatsteNummer() {
    return this._mengmonsters.reduce((max, m) => Math.max(max, Number(/^MM(\d+)$/i.exec(m.code)?.[1]) || 0), 0);
  }

  zetFilter(next) { this._zetFilter({ ...EMPTY_FILTER, ...next }); }

  /** Het vorige filter terug; false als er geen is. */
  vorigFilter() {
    if (!this._historie.length) return false;
    this._filter = this._historie.at(-1);
    this._historie = this._historie.slice(0, -1);
    return true;
  }

  /** In welk mengmonster zit dit potje? De code, of null. */
  zitIn(potjeId) {
    return this._mengmonsters.find((m) => m.potjes.some((p) => p.id === potjeId))?.code ?? null;
  }

  /** Alles wat het filter oplevert en nog in geen mengmonster zit. */
  vrijePotjes() {
    const f = this._filter;
    const o = this.overzichten;
    const potjes = isFilterActive(f) ? matchingPots(o, f) : o.flatMap((x) => x.pots);
    return potjes.filter((p) => !this.zitIn(p.id));
  }

  /** '5 lagen in 3 boringen · 4 potten', voor onder een antwoord van de assistent. */
  telregel(f = this._filter) {
    let lagen = 0;
    const boringen = new Set();
    for (const o of this.overzichten) {
      if (f.boringIds.length && !f.boringIds.includes(o.id)) continue;
      for (const l of visibleLayers(o, f)) if (!rejectReason(l, f)) { lagen += 1; boringen.add(o.id); }
    }
    const potten = matchingPots(this.overzichten, f).length;
    return `${meervoud(lagen, 'laag', 'lagen')} in ${meervoud(boringen.size, 'boring', 'boringen')} · ${meervoud(potten, 'pot', 'potten')}`;
  }

  /**
   * Meerdere potjes tegelijk in een mengmonster.
   *
   * Met `nieuw` komt er een nieuw mengmonster (een overgenomen voorstel is zijn eigen pot);
   * anders gaan ze in het actieve, of in een nieuw als er nog geen is. Potjes die al ergens in
   * zitten blijven waar ze zijn: één potje gaat maar één keer naar het lab.
   *
   * @returns {string|null} de code van het mengmonster waar ze in kwamen
   */
  voegPotjesToe(potjes, nieuw = false) {
    if (this.vergrendeld) return null;
    const vrij = potjes.filter((p) => !this.zitIn(p.id));
    if (!vrij.length) return null;
    let code = nieuw ? null : this._actief;
    let lijst = this._mengmonsters;
    if (!code) {
      code = this._volgendeCode();
      lijst = [...lijst, leegMengmonster(code)];
    }
    const erbij = vrij.map((p) => this._maakPotje(p.boring, p.monster, p.from, p.to));
    this._mengmonsters = lijst.map((m) => (m.code === code ? { ...m, potjes: [...m.potjes, ...erbij] } : m));
    this._zetActief(code);
    this._toonBoringen(vrij.map((p) => p.boring?.id));
    this._meldMengmonsters();
    return code;
  }

  /**
   * Bewerkingen van de assistent op het mandje, in één keer.
   *
   *   add     potjes in mengmonster `code`; zit een potje al ergens anders, dan blijft het daar
   *   remove  potjes uit `code`
   *   move    potjes uit waar ze zitten naar `code`
   *   delete  mengmonster `code` weg (de potjes komen vrij)
   * `code` mag NIEUW1, NIEUW2 ... zijn: dat wordt een nieuw mengmonster met het volgende nummer.
   *
   * Geeft terug wat er gebeurde, per regel, en de mengregels die de geraakte mengmonsters nu
   * breken. Die controle doen wij en niet het model: het model mag een regel vergeten, de
   * waarschuwing hier niet.
   */
  bewerkMandje(ops) {
    if (this.vergrendeld) return { lines: ['Deze labopdracht is verstuurd; de mengmonsters liggen vast.'], warnings: [] };
    let lijst = this._mengmonsters.map((m) => ({ ...m, potjes: [...m.potjes] }));
    const nieuw = new Map();
    const geraakt = new Set();
    const regels = [];
    const naam = (p) => `${p.boringName} ${p.from}–${p.to}`;
    const doel = (code) => {
      if (/^NIEUW\d+$/.test(code)) {
        if (!nieuw.has(code)) {
          const echt = this._volgendeCode(lijst);
          lijst.push(leegMengmonster(echt));
          nieuw.set(code, echt);
        }
        return lijst.find((m) => m.code === nieuw.get(code));
      }
      return lijst.find((m) => m.code === code) || null;
    };
    const waar = (id) => lijst.find((m) => m.potjes.some((p) => p.id === id));

    for (const op of ops) {
      if (op.op === 'delete') {
        if (!lijst.some((m) => m.code === op.code)) continue;
        lijst = lijst.filter((m) => m.code !== op.code);
        geraakt.delete(op.code);
        regels.push(`${op.code} verwijderd`);
        continue;
      }
      const m = doel(op.code);
      if (!m) { regels.push(`${op.code} bestaat niet`); continue; }
      if (op.op === 'remove') {
        const eruit = op.pots.filter((p) => m.potjes.some((x) => x.id === p.id));
        m.potjes = m.potjes.filter((x) => !eruit.some((p) => p.id === x.id));
        if (eruit.length) regels.push(`${m.code}: ${eruit.map(naam).join(', ')} eruit`);
        geraakt.add(m.code);
        continue;
      }
      const erbij = [];
      for (const p of op.pots) {
        const nu = waar(p.id);
        if (nu?.code === m.code) continue;
        if (nu && op.op === 'add') { regels.push(`${naam(p)} zit al in ${nu.code}; niet verplaatst`); continue; }
        if (nu) { nu.potjes = nu.potjes.filter((x) => x.id !== p.id); geraakt.add(nu.code); }
        m.potjes.push(this._maakPotje(p.boring, p.monster, p.from, p.to));
        erbij.push(p);
      }
      if (erbij.length) regels.push(`${m.code}: ${erbij.map(naam).join(', ')} erbij`);
      geraakt.add(m.code);
    }

    this._mengmonsters = lijst;
    const laatste = [...geraakt].filter((c) => lijst.some((m) => m.code === c)).at(-1);
    this._zetActief(laatste || (lijst.some((m) => m.code === this._actief) ? this._actief : lijst.at(-1)?.code ?? null));
    this._meldMengmonsters();
    const warnings = [...geraakt]
      .map((code) => lijst.find((m) => m.code === code))
      .filter(Boolean)
      .map((m) => ({ code: m.code, warnings: this.waarschuwingen(m.code) }))
      .filter((w) => w.warnings.length);
    return { lines: regels, warnings };
  }

  // ── Het mandje ──────────────────────────────────────────────────────────

  /**
   * De code voor een nieuw mengmonster: het laagste MM-nummer dat nog vrij is.
   *
   * Gooi je alles weg, dan begin je weer bij MM01; gooi je MM02 weg, dan krijgt het volgende
   * mengmonster MM02. Dat kan veilig sinds de pagina mengmonsters op hun vaste sleutel bewaart
   * en niet op naam: een nieuw MM02 erft dus niets van het weggegooide.
   */
  _volgendeCode(lijst = this._mengmonsters) {
    const bezet = new Set(lijst.map((m) => String(m.code).toUpperCase()));
    for (let n = 1; ; n += 1) {
      const code = `MM${String(n).padStart(2, '0')}`;
      if (!bezet.has(code)) return code;
    }
  }

  _openHernoem(code) {
    if (this.vergrendeld) return;
    this._hernoemt = code;
    this._hernoemFout = '';
    this.updateComplete.then(() => {
      const veld = this.renderRoot.querySelector('.naamveld');
      veld?.focus();
      veld?.select();
    });
  }

  _bewaarHernoem(code, naam) {
    if (this._hernoemt !== code) return;
    const fout = this.hernoemMengmonster(code, naam);
    if (fout) { this._hernoemFout = fout; return; }
    this._hernoemt = null;
    this._hernoemFout = '';
  }

  /**
   * Een mengmonster een andere naam geven, bijvoorbeeld 'MM01 bovengrond'. Geeft een melding
   * terug als het niet kan (leeg, of al in gebruik), anders null.
   *
   * De naam is ook wat het lab op het certificaat zet, dus twee keer dezelfde naam kan niet:
   * dan weet je bij de uitslag niet welk mengmonster bedoeld is.
   */
  hernoemMengmonster(code, naam) {
    if (this.vergrendeld) return 'De opdracht is verstuurd; de namen liggen vast.';
    const nieuw = schoneNaam(naam);
    if (nieuw === code) return null;
    const fout = this._naamFout(nieuw, code);
    if (fout) return fout;
    this._mengmonsters = this._mengmonsters.map((m) => (m.code === code ? { ...m, code: nieuw } : m));
    if (this._actief === code) { this._actief = nieuw; this._meldActief(); }
    this._meldMengmonsters();
    return null;
  }

  /** Waarom deze naam niet kan (leeg, te lang, al in gebruik), of null. `behalve`: de eigen code. */
  _naamFout(naam, behalve = null) {
    if (!naam) return 'Een mengmonster heeft een naam nodig.';
    if (naam.length > 40) return 'Hoogstens 40 tekens.';
    if (this._mengmonsters.some((m) => m.code !== behalve && m.code.toLowerCase() === naam.toLowerCase())) {
      return `${naam} bestaat al.`;
    }
    return null;
  }

  _zetActief(code) {
    if (this._actief === code) return;
    this._actief = code;
    this._meldActief();
  }

  /**
   * Een potje aanklikken: erin of eruit, bij het actieve mengmonster.
   *
   * Is er nog geen mengmonster, dan maakt de eerste klik er een. Dat scheelt een knop die je
   * altijd eerst moet indrukken, en de bedoeling is niet dubbelzinnig: je klikt een potje aan
   * omdat je het ergens in wilt hebben.
   */
  _klikPotje(boring, monster, van, tot) {
    if (this.vergrendeld) return;
    const id = potIdOf(boring, monster);
    const zit = this.zitIn(id);

    // Zit hij al in een ánder mengmonster, dan doet deze klik niets: één potje gaat maar één
    // keer naar het lab. De tegel is in dat geval al uitgeschakeld; dit is de vangnetregel.
    if (zit && zit !== this._actief) return;

    if (zit) {
      this._mengmonsters = this._mengmonsters.map((m) =>
        m.code === zit ? { ...m, potjes: m.potjes.filter((p) => p.id !== id) } : m);
      this._meldMengmonsters();
      return;
    }

    let code = this._actief;
    let lijst = this._mengmonsters;
    if (!code) {
      code = this._volgendeCode();
      lijst = [...lijst, leegMengmonster(code)];
      this._zetActief(code);
    }
    const potje = this._maakPotje(boring, monster, van, tot);
    this._mengmonsters = lijst.map((m) => (m.code === code ? { ...m, potjes: [...m.potjes, potje] } : m));
    this._meldMengmonsters();
  }

  /** Wat het mandje over een potje bewaart; zie de toelichting per veld. */
  _maakPotje(boring, monster, van, tot) {
    return {
      id: potIdOf(boring, monster),
      naam: monster.name || monster.identificatie || 'monster',
      boringId: boring.id,
      boringNaam: boring.name || 'Naamloos',
      // De barcode is `identificatie` en niet de naam: het lab scant de pot, en 'B-01-1' staat
      // daar niet op.
      verpakking: monster.packingType || null,
      barcode: monster.identificatie || null,
      datum: monster.datum || null,
      // Geroerd (schepmonster) of ongeroerd (steekbus): het lab behandelt een steekbus anders.
      geroerd: monster.geroerd !== false,
      van, tot,
      // Grond of grondwater volgt uit het potje zelf; de gebruiker kiest dat niet apart.
      matrix: /water|fles/i.test(String(monster.type || '')) ? 'water' : 'grond',
    };
  }

  /**
   * Een mengmonster kiezen: dat wordt het actieve, en de boringen waar zijn potjes uit komen
   * worden geselecteerd. Zo zie je meteen waar het vandaan komt, en staan precies die
   * boorprofielen naast elkaar. Een leeg mengmonster laat de selectie staan.
   */
  kiesMengmonster(code) {
    const m = this._mengmonsters.find((x) => x.code === code);
    if (!m) return;
    this._zetActief(code);
    this._toonBoringen(m.potjes.map((p) => p.boringId), true);
  }

  /** Deze boringen in de selectie (of als selectie), en de kaart erop laten inzoomen. */
  _toonBoringen(ids, vervang = false) {
    const bekend = new Set(this.inPeriode.map((b) => b.id));
    const erin = [...new Set(ids)].filter((id) => id && bekend.has(id));
    if (!erin.length) return;
    this._selectie = new Set(vervang ? erin : [...this._selectie, ...erin]);
    const punten = this.inPeriode.filter((b) => this._selectie.has(b.id) && heeftPlek(b));
    // Alleen als de kaart zichtbaar is: een verborgen kaart heeft geen maat, en Leaflet rekent
    // dan een onzinnig zoomniveau uit.
    if (this._kaart && this.zichtbaar && punten.length) {
      this._kaart.fitBounds(L.latLngBounds(punten.map((b) => [Number(b.y), Number(b.x)])), { padding: [40, 40], maxZoom: 18 });
    }
  }

  /**
   * Een nieuw, leeg mengmonster dat meteen het actieve is. Zonder naam het volgende vrije
   * MM-nummer; met een naam die naam (een naam die niet kan, geeft null).
   */
  nieuwMengmonster(naam = null) {
    if (this.vergrendeld) return null;
    const gekozen = naam == null ? null : schoneNaam(naam);
    if (gekozen != null && this._naamFout(gekozen)) return null;
    const code = gekozen ?? this._volgendeCode();
    this._mengmonsters = [...this._mengmonsters, leegMengmonster(code)];
    this._zetActief(code);
    this._meldMengmonsters();
    return code;
  }

  verwijderMengmonster(code) {
    if (this.vergrendeld) return;
    // Alle potjes komen weer vrij; ze zijn nergens anders aan vast.
    this._mengmonsters = this._mengmonsters.filter((m) => m.code !== code);
    if (this._actief === code) this._zetActief(this._mengmonsters.at(-1)?.code ?? null);
    this._meldMengmonsters();
  }

  /** '+ Analysemonster': eerst een naam (voorstel: het volgende MM-nummer), dan pas aanmaken. */
  _startNieuw() {
    if (this.vergrendeld) return;
    this._nieuwNaam = this._volgendeCode();
    this._nieuwFout = '';
    this.updateComplete.then(() => {
      const veld = this.renderRoot.querySelector('.nieuwveld');
      veld?.focus();
      veld?.select();
    });
  }

  _bevestigNieuw(naam) {
    const schoon = schoneNaam(naam);
    const fout = this._naamFout(schoon);
    if (fout) { this._nieuwFout = fout; return; }
    this.nieuwMengmonster(schoon);
    this._nieuwNaam = null;
    this._nieuwFout = '';
  }

  _annuleerNieuw() {
    this._nieuwNaam = null;
    this._nieuwFout = '';
  }

  /** Het analysemonster waar dit watermonster in zit, of null. */
  _waterIn(w) {
    const id = w.wm.id || w.wm.name;
    return this._mengmonsters.find((m) => m.potjes.some((p) => isBottleOf(p.id, id))) || null;
  }

  /**
   * Een watermonster aanklikken: het wordt een eigen analysemonster met al zijn flessen, met de
   * naam van het watermonster. Nog een keer klikken haalt het analysemonster weer weg.
   * Grondwater wordt niet gemengd, dus er gaat nooit iets bij een bestaand analysemonster.
   */
  _klikWatermonster(w) {
    if (this.vergrendeld) return;
    const zit = this._waterIn(w);
    if (zit) { this.verwijderMengmonster(zit.code); return; }
    if (!w.potjes.length) return;
    let naam = schoneNaam(w.wm.name) || this._volgendeCode();
    for (let i = 2; this._naamFout(naam); i++) naam = `${schoneNaam(w.wm.name) || 'Watermonster'} (${i})`;
    this._mengmonsters = [...this._mengmonsters, { ...leegMengmonster(naam), potjes: w.potjes.map((p) => ({ ...p })) }];
    this._zetActief(naam);
    this._meldMengmonsters();
  }

  /** Verwijderen met × : een leeg mengmonster meteen, een met potjes na bevestiging. */
  async _vraagVerwijder(m) {
    if (this.vergrendeld) return;
    if (m.potjes.length && !await confirmDialog({
      title: `${m.code} verwijderen?`,
      message: `De ${meervoud(m.potjes.length, 'pot komt', 'potten komen')} weer vrij.`,
      confirmLabel: 'Verwijderen', danger: true,
    })) return;
    this.verwijderMengmonster(m.code);
  }

  /**
   * Waarom juist deze potjes samen gaan, in de woorden van degene die het besloot. Dat staat
   * nergens anders, en een half jaar later is het het eerste wat je wilt weten.
   */
  zetNotitie(code, tekst) {
    if (this.vergrendeld) return;
    this._mengmonsters = this._mengmonsters.map((m) => (m.code === code ? { ...m, notitie: tekst } : m));
    this._meldMengmonsters();
  }

  /**
   * Welke mengregels dit mengmonster breekt: te veel potten, andere grondsoort, andere
   * bijmengingen of intensiteit, asfalt of puin bij grond, enzovoort. Geen blokkade -- de
   * adviseur kan een reden hebben -- maar wel zichtbaar, zodat het een keuze is en geen vergissing.
   *
   * Potjes waarvan de boring niet meer bestaat tellen mee als het om het aantal gaat; voor de
   * rest kennen we hun lagen niet.
   */
  waarschuwingen(code) {
    const m = this._mengmonsters.find((x) => x.code === code);
    if (!m) return [];
    const potten = m.potjes.map((p) => {
      const boring = (this.borings || []).find((b) => b.id === p.boringId);
      return boring ? this._overzicht(boring).pots.find((x) => x.id === p.id) || null : null;
    }).filter(Boolean);
    const water = m.potjes.filter((p) => isWaterMatrix(p.matrix));
    const grond = m.potjes.length - water.length;
    // Grondwater: geen mengregels (je mengt niet), wel de soort van de opdracht en de barcodes.
    if (this._waterModus || water.length) {
      return [
        ...(this._waterModus && grond ? [`${meervoud(grond, 'grondpotje', 'grondpotjes')}; grond gaat in een labopdracht met monstertype Grond`] : []),
        ...(!this._waterModus ? [`${meervoud(water.length, 'fles', 'flessen')} grondwater; grondwater gaat in een eigen labopdracht met monstertype Grondwater`] : []),
        ...(water.some((p) => !p.barcode) ? [`${meervoud(water.filter((p) => !p.barcode).length, 'fles heeft', 'flessen hebben')} geen barcode`] : []),
      ];
    }
    const strategie = potten.map((p) => p.deellocatie).filter((d) => d?.max != null).sort((a, b) => a.max - b.max)[0]?.strategie || null;
    const teVeel = tooManyPotsWarning(m.potjes.length, maxPotsFor(potten), strategie);
    return [
      ...(teVeel ? [teVeel] : []),
      ...this._verkeerdType(potten),
      ...mixingWarnings(potten).filter((w) => !/ potten; .*hoogstens \d+ in één mengmonster$/.test(w)),
    ];
  }

  /**
   * Past het potje bij het monstertype van de opdracht? Het lab analyseert een opdracht als één
   * monstersoort: een asfaltpot in een grondopdracht krijgt grondpakketten, en omgekeerd. Dat
   * zie je pas aan een uitslag die nergens op slaat, dus liever hier.
   */
  _verkeerdType(potten) {
    const asfaltOpdracht = this.monstertype === 'asfalt';
    const vreemd = potten.filter((p) => (potKind(p) === 'asfalt') !== asfaltOpdracht).length;
    if (!vreemd) return [];
    return [asfaltOpdracht
      ? `${meervoud(vreemd, 'potje is', 'potjes zijn')} geen asfalt; die horen in een labopdracht voor grond`
      : `${meervoud(vreemd, 'asfaltpotje', 'asfaltpotjes')}; asfalt gaat in een eigen labopdracht met monstertype Asfalt`];
  }

  _meldMengmonsters() {
    this._eigenMelding = true;
    this.dispatchEvent(new CustomEvent('mengmonsters-changed', {
      detail: { mengmonsters: this._mengmonsters.map((m) => ({ ...m, potjes: [...m.potjes] })) },
      bubbles: true, composed: true,
    }));
  }

  /** Het actieve mengmonster is ook op het tabblad Pakketten te zien ('vult aan'). */
  _meldActief() {
    this.dispatchEvent(new CustomEvent('actief-changed', {
      detail: { code: this._actief }, bubbles: true, composed: true,
    }));
  }

  // ── De kaart ────────────────────────────────────────────────────────────

  updated(changed) {
    if (changed.has('_dlBewerkt') && this._dlBewerkt && this._venster) this._openDeellocatieVenster();
    // Staan de profielen in een apart venster, dan krijgt dat na elke herteking de nieuwe stand.
    if (this._pvVenster) this._stuurProfielen();
    // Na elke herteking: passen de BZB's nog in hun laag? (zie _pasBzbIn)
    pasBzbIn(this.renderRoot);
    if (!this._bzbRO) {
      // Andere breedte (scherm, kaart in- of uitgeklapt): opnieuw passen.
      this._bzbRO = new ResizeObserver(() => pasBzbIn(this.renderRoot));
      this._bzbRO.observe(this);
    }
    if (changed.has('projectId')) this._loadSublocations();
    if (!this._kaart) this._maakKaart();
    if (!this._kaart) return;
    if (['_sublocations', '_selectie', '_periode', '_periodeVan', '_periodeTot', '_boringFilter'].some((k) => changed.has(k))) this._drawSublocations();
    // Weer opengeklapt: de kaart stond verborgen en weet zijn maat niet meer.
    if (changed.has('_kaartOpen') && this._kaartOpen) this._kaart.invalidateSize();
    if (changed.has('zichtbaar') && this.zichtbaar) {
      // Terug op dit tabblad: de kaart stond verborgen en heeft dan geen maat meer.
      this._kaart.invalidateSize();
      if (!this._kaartIngepast) { this._kaartIngepast = true; this._tekenMarkers(true); return; }
    }
    if (['borings', '_periode', '_periodeVan', '_periodeTot', '_boringFilter', '_selectie', '_filter', '_actief', '_mengmonsters'].some((k) => changed.has(k))) {
      this._tekenMarkers(changed.has('borings') && !this._kaartIngepast && this.zichtbaar);
    }
  }

  _maakKaart() {
    const doel = this.renderRoot?.querySelector('#kaart');
    if (!doel || !doel.isConnected) return;
    if (!MAPTILER_KEY) console.warn('[lab-mengmonsters] VITE_MAPTILER_KEY ontbreekt — de basiskaart laadt niet.');

    this._kaart = L.map(doel, { zoomControl: true, attributionControl: true, boxZoom: false });
    const NL = L.latLngBounds([[50.5, 3.0], [53.8, 7.5]]);
    L.tileLayer(TILE_WORLD, { maxZoom: 21, attribution: MT_ATTR }).addTo(this._kaart);
    L.tileLayer(TILE_BRT, { bounds: NL, maxZoom: 21, maxNativeZoom: 19, attribution: PDOK_ATTR }).addTo(this._kaart);
    this._kaart.attributionControl.setPrefix(false);
    this._dlLaag = L.layerGroup().addTo(this._kaart);
    this._drawSublocations();
    this._laag = L.layerGroup().addTo(this._kaart);
    this._kaart.setView([52.1, 5.3], 7);
    // Inpassen kan pas als de kaart zichtbaar is; open je de opdracht op een ander tabblad,
    // dan gebeurt het de eerste keer dat je hier komt (zie updated).
    this._kaartIngepast = !!this.zichtbaar;
    this._tekenMarkers(this._kaartIngepast);
    setTimeout(() => this._kaart?.invalidateSize(), 60);
    // Elke maatwijziging (slepen, naast of onder de profielen, het venster) meteen doorgeven:
    // Leaflet meet zichzelf niet opnieuw, en dan blijven er grijze vlakken staan.
    this._kaartRO = new ResizeObserver(() => this._kaart?.invalidateSize());
    this._kaartRO.observe(doel);
    this._koppelSelectie();
    this._zetKaartModus(this._kaartModus);
  }

  /**
   * Slepen over de kaart trekt een kader en selecteert wat erin valt.
   *
   * Slepen is op een kaart normaal gesproken verschuiven, en dat kun je niet zomaar afpakken.
   * Daarom staat er een schakelaar in de hoek: 'Selecteren' (de stand waarin je hier begint,
   * want daarvoor kom je op dit scherm) en 'Verplaatsen'. Zoomen met het wiel werkt in beide.
   *
   * Met shift, ctrl of cmd ingedrukt komt het kader bij de bestaande selectie; zonder vervangt het
   * die. Een sleep korter dan een paar pixels is een klik: die laat de selectie met rust, zodat
   * een misklik op de kaart niet je hele selectie wist en een klik op een boring blijft werken.
   */
  _koppelSelectie() {
    this._ontkoppelKader?.();
    this._ontkoppelKader = koppelKader(this._kaart, {
      magSelecteren: () => this._kaartModus === 'selecteren',
      // Ook buiten de selecteerstand: een klik op een deellocatie kijkt hiernaar of het een
      // klik was of het einde van een sleep.
      opDruk: (e) => { this._drukPunt = e.containerPoint; },
      bijKader: (grens, erbij) => {
        const erin = this.inPeriode.filter(heeftPlek).filter((b) => grens.contains([Number(b.y), Number(b.x)]));
        this._selectie = erbij
          ? new Set([...this._selectie, ...erin.map((b) => b.id)])
          : new Set(erin.map((b) => b.id));
      },
    });
  }

  // ── Deellocaties ────────────────────────────────────────────────────────

  _loadSublocations() {
    const id = this.projectId;
    if (!id) { this._sublocations = []; return; }
    loadSublocations(id)
      .then((list) => { if (this.projectId === id) this._sublocations = list; })
      .catch((e) => console.warn('[lab-mengmonsters] deellocaties laden mislukt:', e.message));
  }

  /**
   * Een klik op een deellocatie selecteert de boringen erin, net als een kader; met shift,
   * ctrl of cmd komen ze bij de selectie. Hernoemen en verwijderen doe je op de projectkaart.
   */
  _drawSublocations() {
    if (!this._dlLaag) return;
    this._stuurStand();
    drawSublocations(this._dlLaag, this._sublocations, {
      bubbling: true,
      highlightId: this._actieveDeellocatie()?.id ?? null,
      onClick: (s, e) => {
        if (this._dlMode) return;
        // Het einde van een kader dat in het vlak begon, is geen klik op het vlak.
        if (this._drukPunt && this._drukPunt.distanceTo(e.containerPoint) >= KLIK_DREMPEL_PX) return;
        this._kiesDeellocatie(s, erbijToets(e.originalEvent));
      },
    });
  }

  /** De boringen (in de gekozen periode) die bij deze deellocatie horen. */
  _boringenIn(s) {
    // De opgeslagen toewijzing (koppeltabel), ook zonder coördinaat; het vlak beslist niets.
    // Een boring kan in meer deellocaties liggen. Toewijzen gebeurt op de projectkaart.
    return this.inPeriode.filter((b) => (b.sublocationIds || []).includes(s.id));
  }

  /**
   * Selecteer de boringen van een deellocatie, of -- met shift/ctrl/cmd -- tel ze bij de selectie op.
   * Nog een keer op dezelfde deellocatie klikken zet de selectie weer leeg.
   */
  _kiesDeellocatie(s, erbij = false) {
    const ids = this._boringenIn(s).map((b) => b.id);
    if (erbij) { this._selectie = new Set([...this._selectie, ...ids]); return; }
    this._selectie = this._actieveDeellocatie()?.id === s.id ? new Set() : new Set(ids);
  }

  /** De deellocatie waarvan precies de boringen geselecteerd zijn, of null. */
  _actieveDeellocatie() {
    if (!this._selectie?.size) return null;
    return this._sublocations.find((s) => {
      const ids = this._boringenIn(s).map((b) => b.id);
      return ids.length === this._selectie.size && ids.every((id) => this._selectie.has(id));
    }) || null;
  }

  /**
   * Eén knop per deellocatie boven de kaart. Zo kies je een deellocatie ook als de kaart is
   * ingeklapt, en zie je meteen hoeveel boringen erin liggen.
   */
  _renderDeellocatieKeuze() {
    if (!this._sublocations.length) return '';
    const actief = this._actieveDeellocatie();
    return html`
      <div class="dlrij">
        <span class="naarlabel">Deellocaties</span>
        ${this._sublocations.map((s) => {
          const n = this._boringenIn(s).length;
          if (this._dlBeheer) {
            if (this._dlHernoemt === s.id) {
              return html`
                <input class="naamveld dlnaamveld" .value=${s.name} maxlength="60" aria-label=${`Nieuwe naam voor ${s.name}`}
                       @keydown=${(e) => {
                         if (e.key === 'Enter') { e.preventDefault(); this._hernoemDeellocatie(s, e.target.value); }
                         if (e.key === 'Escape') { this._dlHernoemt = null; this._dlBeheerFout = ''; }
                       }}
                       @blur=${(e) => this._hernoemDeellocatie(s, e.target.value)}>`;
            }
            return html`
              <span class="dlbeheer">
                <button class="dlchip beheer" title=${`${s.name} bewerken: naam, omschrijving en welke boringen erbij horen`}
                        @click=${() => this._openDeellocatieBewerken(s)}>
                  ${unsafeSVG(lucideIcon(Pencil, 11))} ${s.name}<span class="aantal">${n}</span>
                </button>
                <button class="chipactie weg" title=${`${s.name} verwijderen (de boringen blijven)`} aria-label=${`${s.name} verwijderen`}
                        @click=${() => this._verwijderDeellocatie(s)}>${unsafeSVG(lucideIcon(X, 12))}</button>
              </span>`;
          }
          return html`
            <button class="dlchip ${actief?.id === s.id ? 'aan' : ''}" ?disabled=${!n}
                    title=${n ? `Selecteer de ${meervoud(n, 'boring', 'boringen')} in ${s.name} · met shift (of ctrl) tel je ze bij je selectie op`
                              : `Geen boringen in ${s.name}`}
                    @click=${(e) => this._kiesDeellocatie(s, erbijToets(e))}>
              ${s.name}<span class="aantal">${n}</span>
            </button>`;
        })}
        ${this.projectId ? html`
          <button class="tekstknop ${this._dlBeheer ? 'groen' : ''}"
                  title=${this._dlBeheer ? 'Klaar met beheren' : 'Deellocaties bewerken, boringen toekennen of verwijderen'}
                  @click=${() => { this._dlBeheer = !this._dlBeheer; this._dlHernoemt = null; this._dlBeheerFout = ''; }}>
            ${this._dlBeheer ? 'Klaar' : 'Beheren'}</button>` : ''}
        ${this._dlBeheerFout ? html`<span class="dlbeheerfout">${this._dlBeheerFout}</span>` : ''}
      </div>`;
  }

  /**
   * Achter 'Beheren': hetzelfde bewerkvenster als op de projectkaart, en net als daar rechts
   * over de kaart. Zo ken je boringen aan een deellocatie toe zonder de labopdracht te verlaten,
   * en zie je het vlak ernaast. Staat de kaart in een apart venster, dan opent het hier als
   * venster over de pagina.
   */
  _openDeellocatieBewerken(s) {
    this._dlBewerkt = s.id;
    if (this._venster) return;
    if (!this._kaartOpen) this._zetPaneel('kaart', true);
    if (s.geometry?.length >= 3) {
      this.updateComplete.then(() => {
        if (!this._kaart) return;
        this._kaart.invalidateSize();
        const rechts = Math.min(450, this._kaart.getSize().x / 2);
        this._kaart.fitBounds(sublocationBounds(s), { paddingTopLeft: [40, 50], paddingBottomRight: [rechts, 40], maxZoom: 19 });
      });
    }
  }

  _openDeellocatieVenster() {
    const d = this.renderRoot?.querySelector('dialog.dlvenster');
    if (d && !d.open) d.showModal();
  }

  get _bewerkteDeellocatie() {
    return this._dlBewerkt ? this._sublocations.find((x) => x.id === this._dlBewerkt) || null : null;
  }

  _deellocatieEditor(s, klasse = '') {
    const borings = (this.borings || []).map((b) => {
      const lat = Number(b.y), lng = Number(b.x);
      const ok = b.x != null && b.y != null && Number.isFinite(lat) && Number.isFinite(lng) && Math.abs(lng) <= 180 && Math.abs(lat) <= 90;
      return { guid: b.id, name: b.name || '?', lat: ok ? lat : null, lng: ok ? lng : null, sublocationIds: b.sublocationIds || [] };
    });
    return html`
      <sublocation-editor class=${klasse} .projectId=${this.projectId} .sublocation=${s} .sublocations=${this._sublocations} .borings=${borings}
                          @sublocations-assigned=${(e) => { e.stopPropagation(); this._deellocatiesToegekend(e.detail?.changes || {}); }}
                          @sublocation-deleted=${(e) => { this._deellocatieWeg(e.detail.id); this._dlBewerkt = null; }}
                          @close=${() => { this._dlBewerkt = null; }}></sublocation-editor>`;
  }

  /** Alleen als de kaart in een apart venster staat; anders staat het bewerkvenster op de kaart. */
  _renderDeellocatieVenster() {
    const s = this._bewerkteDeellocatie;
    if (!s || !this._venster) return '';
    return html`
      <dialog class="dlvenster" @cancel=${(e) => { e.preventDefault(); this._dlBewerkt = null; }}
              @click=${(e) => { if (e.target === e.currentTarget) this._dlBewerkt = null; }}>
        ${this._deellocatieEditor(s)}
      </dialog>`;
  }

  /** Een toewijzing uit het bewerkvenster: meteen in de boringen, de kaart en de deellocatieknoppen. */
  _deellocatiesToegekend(changes) {
    for (const b of this.borings || []) {
      if (Object.prototype.hasOwnProperty.call(changes, b.id)) b.sublocationIds = changes[b.id];
    }
    this.requestUpdate();
    this._tekenMarkers();
  }

  _deellocatieWeg(id) {
    for (const b of this.borings || []) {
      if ((b.sublocationIds || []).includes(id)) b.sublocationIds = b.sublocationIds.filter((x) => x !== id);
    }
    this.requestUpdate();
    this._tekenMarkers();
  }

  _openDlHernoem(s) {
    this._dlHernoemt = s.id;
    this._dlBeheerFout = '';
    this.updateComplete.then(() => {
      const veld = this.renderRoot.querySelector('.dlnaamveld');
      veld?.focus();
      veld?.select();
    });
  }

  /**
   * Een deellocatie een andere naam geven, hier of vanuit het kaartvenster. Geeft een melding
   * terug als het niet lukt (leeg, al in gebruik, netwerk), anders null.
   */
  async _hernoemDeellocatieOpslaan(s, naam) {
    const nieuw = String(naam || '').trim().replace(/\s+/g, ' ');
    if (!nieuw) return 'Geef de deellocatie een naam.';
    if (nieuw === s.name) return null;
    try {
      await saveSublocation(this.projectId, s.id, { name: nieuw });
      return null;
    } catch (e) {
      return e.message || 'Hernoemen mislukt.';
    }
  }

  async _hernoemDeellocatie(s, naam) {
    if (this._dlHernoemt !== s.id) return;
    this._dlHernoemt = null;
    const fout = await this._hernoemDeellocatieOpslaan(s, naam);
    this._dlBeheerFout = fout || '';
    if (fout) this._openDlHernoem(s);
  }

  /** Een deellocatie verwijderen, hier of vanuit het kaartvenster (dat vraagt zelf om bevestiging). */
  async _verwijderDeellocatieOpslaan(s) {
    try {
      await removeSublocation(this.projectId, s.id);
      // De boringen blijven; alleen de koppeling met deze deellocatie gaat weg.
      for (const b of this.borings || []) {
        if ((b.sublocationIds || []).includes(s.id)) b.sublocationIds = b.sublocationIds.filter((id) => id !== s.id);
      }
      this.requestUpdate();
      this._tekenMarkers();
      return null;
    } catch (e) {
      return e.message || 'Verwijderen mislukt.';
    }
  }

  async _verwijderDeellocatie(s) {
    const n = this._boringenIn(s).length;
    if (!await confirmDialog({
      title: `Deellocatie ${s.name} verwijderen?`,
      message: n ? `De ${meervoud(n, 'boring blijft', 'boringen blijven')} staan.` : 'Er liggen geen boringen in.',
      confirmLabel: 'Verwijderen', danger: true,
    })) return;
    this._dlBeheerFout = (await this._verwijderDeellocatieOpslaan(s)) || '';
  }

  /** Alle deellocaties van een boring, in de volgorde van de lijst (DL01, DL02, ...). */
  _sublocationsOf(boring) {
    const ids = boring?.sublocationIds || [];
    return this._sublocations.filter((s) => ids.includes(s.id));
  }

  _startDrawing() {
    if (!this._kaart || !this.projectId) return;
    this._dlMode = 'drawing';
    this._dlPoints = 0;
    this._dlError = '';
    // Tekenen gaat met klikken; verschuiven met slepen mag dan gewoon.
    this._modusVoorTekenen = this._kaartModus;
    this._zetKaartModus('verplaatsen');
    if (!this._drawer) {
      this._drawer = new PolygonDrawer(this._kaart, {
        onChange: (n) => { this._dlPoints = n; },
        onFinish: (ring) => this._nameDrawing(ring),
      });
    }
    this._drawer.start();
  }

  async _nameDrawing(ring) {
    this._dlRing = ring;
    this._dlMode = 'naming';
    this._dlName = nextSublocationName(this._sublocations);
    this._dlPreview = L.polygon(ring.map(([lng, lat]) => [lat, lng]), {
      color: '#c2410c', weight: 2, fillColor: '#c2410c', fillOpacity: 0.16, interactive: false,
    }).addTo(this._kaart);
    await this.updateComplete;
    const input = this.renderRoot.querySelector('.dlbalk input');
    input?.focus();
    input?.select();
  }

  _cancelDrawing() {
    this._drawer?.cancel();
    this._dlPreview?.remove();
    this._dlPreview = null;
    this._dlRing = null;
    this._dlMode = null;
    this._dlError = '';
    if (this._modusVoorTekenen) { this._zetKaartModus(this._modusVoorTekenen); this._modusVoorTekenen = null; }
  }

  async _saveDrawing() {
    const name = this._dlName.trim();
    if (!name) { this._dlError = 'Geef de deellocatie een naam.'; return; }
    this._dlBusy = true;
    this._dlError = '';
    try {
      await this._maakDeellocatie(name, this._dlRing);
      this._cancelDrawing();
    } catch (e) {
      this._dlError = e.message || 'Opslaan mislukt.';
    } finally {
      this._dlBusy = false;
    }
  }

  /**
   * Een getekend vlak opslaan als deellocatie, hier of vanuit het kaartvenster. De boringen in
   * het vlak (of binnen 0,5 m van de rand) horen er meteen bij, ook als ze al bij een andere
   * deellocatie horen: een boring kan in meer deellocaties liggen.
   */
  async _maakDeellocatie(name, ring) {
    const created = await saveNewSublocation(this.projectId, { name, geometry: ring });
    const inside = (this.borings || []).filter((b) => heeftPlek(b)
      && inSublocation(Number(b.x), Number(b.y), created));
    if (inside.length) {
      await api.assignSublocationBorings(created.id, inside.map((b) => b.id));
      for (const b of inside) b.sublocationIds = [...(b.sublocationIds || []), created.id];
      this.requestUpdate();
      // De boringen veranderen niet van referentie, dus updated() ziet dit niet: zelf de kaart
      // (en daarmee het kaartvenster) bijwerken, anders telt de nieuwe knop 0 boringen.
      this._drawSublocations();
      this._tekenMarkers();
    }
    return created;
  }

  _zetKaartModus(modus) {
    this._kaartModus = modus;
    // Via classList en niet via een class-binding: Leaflet zet zijn eigen classes op dit element.
    this.renderRoot?.querySelector('#kaart')?.classList.toggle('selecteren', modus === 'selecteren');
    if (!this._kaart) return;
    // In de selecteerstand mag de kaart zelf niet meebewegen, anders sleep je het kaartbeeld
    // onder je kader vandaan.
    if (modus === 'selecteren') this._kaart.dragging.disable();
    else this._kaart.dragging.enable();
  }

  _tekenMarkers(pasIn = false) {
    if (!this._laag) return;
    this._laag.clearLayers();
    this._markers = new Map();
    const punten = this.inPeriode.filter(heeftPlek);
    const gekozen = new Set(this.gekozen.map((b) => b.id));
    const standPunten = [];
    // De boringen die al potjes leveren aan het mengmonster dat je vult: lichtgroen, zodat je
    // ziet waar het vandaan komt zonder ze te hoeven selecteren.
    const actief = this._mengmonsters.find((m) => m.code === this._actief);
    const leveren = new Set((actief?.potjes || []).map((p) => p.boringId));
    for (const b of punten) {
      const aan = gekozen.has(b.id);
      const klasse = ['mm-punt', aan ? 'aan' : leveren.has(b.id) ? 'levert' : '', this._boringPast(b) ? '' : 'gedimd']
        .filter(Boolean).join(' ');
      const naam = (b.name || '?').replace(/[<>&"]/g, '');
      // Alleen bij de gekozen boringen een label: 24 labels over elkaar maken de kaart
      // onleesbaar, terwijl je juist je selectie wilt aflezen.
      const marker = L.marker([Number(b.y), Number(b.x)], {
        icon: L.divIcon({
          className: '',
          html: `<span class="${klasse}" title="${naam}"></span>` + (aan ? `<span class="mm-naam">${naam}</span>` : ''),
          iconSize: null,
        }),
        zIndexOffset: aan ? 1000 : 0,
      });
      marker.on('click', () => this._wisselBoring(b));
      marker.addTo(this._laag);
      this._markers.set(b.id, marker);
      if (b.id === this._gemarkeerd) marker.getElement()?.querySelector('.mm-punt')?.classList.add('hover');
      standPunten.push({ id: b.id, naam: b.name || '?', lat: Number(b.y), lng: Number(b.x), klasse, aan });
    }
    this._standPunten = standPunten;
    this._stuurStand(pasIn);
    if (pasIn && punten.length) {
      this._kaart.fitBounds(L.latLngBounds(punten.map((b) => [Number(b.y), Number(b.x)])), { padding: [28, 28], maxZoom: 18 });
    }
  }

  /**
   * De boring die je in de profielen aanwijst, licht op de kaart op.
   *
   * Bewust buiten Lit om: de markers staan in Leaflet en de grafiek is een paar duizend
   * knopen groot. Alles opnieuw tekenen bij elke muisbeweging zou het scherm laten haperen.
   */
  _markeerOpKaart(id) {
    if (this._gemarkeerd === id) return;
    this._gemarkeerd = id;
    if (this._venster) this._kanaal?.postMessage({ type: 'markeer', id });
    for (const [boringId, marker] of this._markers || []) {
      marker.getElement()?.querySelector('.mm-punt')?.classList.toggle('hover', boringId === id);
    }
    this.renderRoot?.querySelector('#kaart')?.classList.toggle('markeert', !!id);
  }

  // ── De kaart op een tweede scherm ───────────────────────────────────────
  //
  // Idee van RSK: de kaart in een eigen venster, zodat het hele scherm hier voor de profielen
  // is. Deze pagina blijft de baas; het venster tekent wat wij sturen en meldt wat je erop
  // aanklikt. Zie utils/lab-kaart.js voor de berichten.

  _openVenster() {
    if (this._venster) { this._vensterRef?.focus(); return; }
    if (typeof BroadcastChannel === 'undefined') {
      this._vensterFout = 'Deze browser kan de kaart niet in een apart venster tonen.';
      return;
    }
    const sleutel = Math.random().toString(36).slice(2, 10);
    const ref = window.open(`/kaartvenster?k=${sleutel}`, `mv-lab-kaart-${sleutel}`, 'popup,width=1280,height=860');
    if (!ref) {
      this._vensterFout = 'De browser hield het venster tegen. Sta pop-ups toe voor deze site en probeer het opnieuw.';
      return;
    }
    this._vensterFout = '';
    this._vensterRef = ref;
    this._kanaal = new BroadcastChannel(kanaalNaam(sleutel));
    this._kanaal.onmessage = (e) => this._vanVenster(e.data || {});
    // Vangnet voor een venster dat dichtgaat zonder af te melden (een gecrasht tabblad).
    this._vensterCheck = setInterval(() => { if (this._vensterRef?.closed) this._sluitVenster(); }, 1000);
    this._venster = true;
  }

  /** Het venster dicht en de kaart weer hier. */
  _sluitVenster() {
    clearInterval(this._vensterCheck);
    this._vensterCheck = null;
    if (this._kanaal) {
      this._kanaal.postMessage({ type: 'sluit' });
      this._kanaal.close();
    }
    this._kanaal = null;
    try { if (this._vensterRef && !this._vensterRef.closed) this._vensterRef.close(); } catch (_) { /* al weg */ }
    this._vensterRef = null;
    if (this._venster) this._venster = false;
  }

  _vanVenster(m) {
    if (m.type === 'hallo') { this._stuurStand(true); return; }
    if (m.type === 'dicht') { this._sluitVenster(); return; }
    const bekend = new Map(this.inPeriode.map((b) => [b.id, b]));
    if (m.type === 'wissel' && bekend.has(m.id)) this._wisselBoring(bekend.get(m.id));
    if (m.type === 'kader') {
      const ids = (m.ids || []).filter((id) => bekend.has(id));
      this._selectie = m.erbij ? new Set([...this._selectie, ...ids]) : new Set(ids);
    }
    if (m.type === 'deellocatie') {
      const s = this._sublocations.find((x) => x.id === m.id);
      if (s) this._kiesDeellocatie(s, !!m.erbij);
    }
    if (m.type === 'wis-selectie') this._selectie = new Set();
    // Het filter, bediend vanuit het venster: dezelfde acties als het filterblok hier.
    if (m.type === 'filter') {
      const a = this._filterActies;
      if (m.actie === 'patch' && m.waarde && typeof m.waarde === 'object') a.patch(m.waarde);
      if (m.actie === 'boringFilter' && m.waarde && typeof m.waarde === 'object') a.boringFilter(m.waarde);
      if (m.actie === 'wisselBzb' && typeof m.waarde === 'string') a.wisselBzb(m.waarde);
      if (m.actie === 'wisFilters') a.wisFilters();
      if (m.actie === 'periode' && m.waarde && typeof m.waarde === 'object') a.periode(m.waarde);
    }
    if (m.type === 'nieuwe-deellocatie') this._deellocatieUitVenster(m);
    if (m.type === 'deellocatie-hernoem' || m.type === 'deellocatie-verwijder') this._beheerUitVenster(m);
  }

  /** Hernoemen of verwijderen vanuit het kaartvenster; de uitkomst gaat terug naar het venster. */
  async _beheerUitVenster(m) {
    const s = this._sublocations.find((x) => x.id === m.id);
    const fout = !s ? 'Deze deellocatie bestaat niet meer.'
      : m.type === 'deellocatie-hernoem' ? await this._hernoemDeellocatieOpslaan(s, m.name)
        : await this._verwijderDeellocatieOpslaan(s);
    this._kanaal?.postMessage(fout ? { type: 'beheer-fout', verzoek: m.verzoek, message: fout }
      : { type: 'beheer-klaar', verzoek: m.verzoek });
  }

  /** Een vlak dat in het kaartvenster is getekend en benoemd: hier opslaan, uitkomst terugmelden. */
  async _deellocatieUitVenster({ name, ring, verzoek }) {
    const naam = String(name || '').trim();
    try {
      if (!naam) throw new Error('Geef de deellocatie een naam.');
      await this._maakDeellocatie(naam, ring);
      this._kanaal?.postMessage({ type: 'deellocatie-klaar', verzoek });
    } catch (e) {
      this._kanaal?.postMessage({ type: 'deellocatie-fout', verzoek, message: e.message || 'Opslaan mislukt.' });
    }
  }

  /** De stippen en deellocaties zoals ze hier staan, naar het venster. */
  _stuurStand(pasIn = false) {
    if (!this._venster || !this._kanaal) return;
    const actief = this._actieveDeellocatie();
    this._kanaal.postMessage({
      type: 'stand',
      punten: this._standPunten || [],
      sublocations: this._sublocations,
      highlightId: actief?.id ?? null,
      // Alles wat ook bij de kaart hier staat, zodat het venster niets mist.
      knoppen: this._sublocations.map((s) => ({ id: s.id, name: s.name, n: this._boringenIn(s).length })),
      gekozen: this.gekozen.map((b) => b.name || '?'),
      kanTekenen: !!this.projectId,
      volgendeNaam: nextSublocationName(this._sublocations),
      // Het filter, zodat het venster dezelfde filterbalk kan tonen (utils/lab-filterblok.js).
      filter: this._filterModel(this.inPeriode),
      pasIn,
    });
  }

  _wisselBoring(boring) {
    const next = new Set(this._selectie);
    if (next.has(boring.id)) next.delete(boring.id); else next.add(boring.id);
    this._selectie = next;
  }

  /**
   * De gedeelde tooltip van de applicatie, maar pas na 0,3 seconde.
   *
   * Zonder die aanloop flitst hij mee terwijl je over een rij potjes beweegt. Eenmaal in beeld
   * volgt hij wél de cursor, zoals overal elders.
   */
  _tipBeweeg(e) {
    const el = e.target?.closest?.('[data-tip]');
    if (!el) { this._tipWeg(); return; }
    this._tipPunt = { target: el, clientX: e.clientX, clientY: e.clientY };
    if (this._tipAan) { tipMove(this._tipPunt); return; }
    if (this._tipDoel === el) return;
    clearTimeout(this._tipTimer);
    this._tipDoel = el;
    this._tipTimer = setTimeout(() => { this._tipAan = true; tipMove(this._tipPunt); }, 300);
  }

  _tipWeg() {
    clearTimeout(this._tipTimer);
    this._tipTimer = null;
    this._tipDoel = null;
    this._tipAan = false;
    tipHide();
  }

  // ── Weergave ────────────────────────────────────────────────────────────

  render() {
    const inPeriode = this.inPeriode;
    return html`
      ${PATROON_DEFS}
      ${renderBzbKaart(this._bzbKaart, () => { this._bzbKaart = null; })}
      <!-- Veldgegevens links, de mengmonsters rechts ernaast en altijd in beeld: zo zie je wat
           je samenstelt terwijl je door de profielen scrolt (zie _renderMandjekolom). -->
      <div class="werkruimte">
      <section class="paneel">
        <!-- Donkergroen, net als de kop van de mengmonsters rechts: samen één werkplek. -->
        <header class="kop">
          <span class="mv-caps">Veldgegevens</span>
        </header>

        <!-- Filters met een eigen kop, in en uit te klappen zoals de kaart. -->
        <div class="filterkolom">
          <!-- De hele kopbalk klapt in en uit; de knop erin doet niets zelf, zijn klik komt hier aan. -->
          <div class="blokkop sectiekop klikbaar" @click=${() => this._zetPaneel('filter', !this._filterOpen)}>
            ${this._inklapknop(this._filterOpen, 'Filters', () => {},
              // Dicht maar wel actief: dat moet je zien, anders zoek je naar boringen die het filter verbergt.
              !this._filterOpen && this._filterAan ? html`<span class="actieflabel">actief</span>` : '')}
          </div>
          ${this._filterOpen ? this._renderFilter(inPeriode) : ''}
        </div>

        <!-- Kaart boven, profielen en mengmonsters eronder. -->
        <div class="werkvlak ${this._kaartOpen && !this._venster ? '' : 'kaartweg'}">
          ${this._renderKaart()}
          ${this._renderProfielen()}
        </div>
      </section>
      ${this._renderMandjekolom()}
      </div>
      ${this._renderDeellocatieVenster()}
      ${this._renderNotitievenster()}
    `;
  }

  /**
   * Het filter: drie regels, van grof naar fijn. De keuzelijsten bevatten alleen wat in deze
   * periode echt voorkomt; een lijst met honderden codelijstwaarden waarvan er drie iets
   * opleveren, kost meer tijd dan hij bespaart.
   */
  /**
   * Het filterblok; de opmaak staat in utils/lab-filterblok.js, zodat het kaartvenster precies
   * hetzelfde filter toont (zie _stuurStand en het bericht 'filter' in _vanVenster).
   */
  _renderFilter(inPeriode) {
    return renderFilterblok(this._filterModel(inPeriode), this._filterActies);
  }

  /** Staat er een filter aan, de tijd meegerekend? Dan zegt een dichtgeklapte filterkop 'actief'. */
  get _filterAan() {
    return this._periode !== 'alles' || isFilterActive(this._filter);
  }

  /** Wat het filterblok nodig heeft, als gewone data: ook voor het venster op een tweede scherm. */
  _filterModel(inPeriode) {
    const bf = this._boringFilter;
    const weg = this._binnenPeriode().length - inPeriode.length;
    const tijd = {
      periode: this._periode, van: this._periodeVan, tot: this._periodeTot,
      opties: PERIODES.map((p) => [p.id, p.naam]),
    };
    if (this._waterModus) return { water: true, bf, weg, tijd, actief: this._periode !== 'alles' };
    const f = this._filter;
    const overzichten = inPeriode.map((b) => this._overzicht(b));
    // Per keuze in hoeveel (zichtbare) lagen hij voorkomt: dat staat achter elke regel in het menu.
    const tel = () => new Map();
    const soils = tel(), bijm = tel(), bzbAantal = tel(), ligging = tel(), waarneming = tel();
    const op = (m, k) => m.set(k, (m.get(k) || 0) + 1);
    for (const o of overzichten) {
      for (const l of visibleLayers(o, f)) {
        if (l.soil) op(soils, l.soil);
        new Set(l.admixtures).forEach((a) => op(bijm, a));
        for (const t of new Set(l.bzb.map((z) => z.type))) op(bzbAantal, t);
        if (l.position) op(ligging, l.position);
        if (l.odor) op(waarneming, 'odor');
        if (l.oil) op(waarneming, 'oil');
        if (!l.odor && !l.oil) op(waarneming, 'none');
      }
    }
    // Een gekozen waarde blijft in de lijst staan, ook als hij na een ander filter wegvalt.
    for (const t of [...f.include, ...f.exclude]) if (!bzbAantal.has(t)) bzbAantal.set(t, 0);
    for (const v of f.soils) if (!soils.has(v)) soils.set(v, 0);
    for (const v of f.admixtures) if (!bijm.has(v)) bijm.set(v, 0);
    // Verpakking telt potjes, niet lagen: 'Steekbus 4' zijn vier steekbussen.
    const verpakking = new Map();
    for (const o of overzichten) for (const p of o.pots) verpakking.set(p.packaging, (verpakking.get(p.packaging) || 0) + 1);
    for (const v of f.packaging) if (!verpakking.has(v)) verpakking.set(v, 0);
    const volgorde = Object.keys(PACKAGING_LABELS);

    let inSel = 0, passend = 0;
    for (const b of this.gekozen) {
      const o = this._overzicht(b);
      o.layers.forEach((l, i) => {
        if (f.sampledOnly && !l.pots.length) return;
        inSel += 1;
        if (this._laagPast(b, i)) passend += 1;
      });
    }
    return {
      water: false, bf, weg, f, tijd,
      soils: [...soils.keys()].sort(opNaam).map((v) => [v, soils.get(v)]),
      bijm: [...bijm.keys()].sort(opNaam).map((v) => [v, bijm.get(v)]),
      // Op groep, en binnen een groep op naam.
      bzb: [...bzbAantal.keys()].map((t) => [t, bzbAantal.get(t), bzbGroep(t)])
        .sort((a, b) => BZB_GROEP_VOLGORDE.indexOf(a[2]) - BZB_GROEP_VOLGORDE.indexOf(b[2]) || opNaam(a[0], b[0])),
      grondwater: ['above', 'below', 'across'].map((k) => [k, POSITION_LABELS[k].replace(/^./, (c) => c.toUpperCase()), ligging.get(k) || 0]),
      waarneming: [['odor', 'Met geur'], ['oil', 'Met olie'], ['none', 'Zonder geur of olie']].map(([k, label]) => [k, label, waarneming.get(k) || 0]),
      verpakking: [...verpakking.keys()].sort((a, b) => volgorde.indexOf(a) - volgorde.indexOf(b)).map((v) => [v, verpakking.get(v)]),
      boringen: inPeriode.map((b) => [b.id, b.name || '?']).sort((a, b) => a[1].localeCompare(b[1], 'nl', { numeric: true })),
      actief: this._filterAan,
      passend, inSel,
      heeftGekozen: this.gekozen.length > 0,
    };
  }

  /** De knoppen van het filterblok op deze pagina. */
  get _filterActies() {
    return {
      patch: (p) => this._patch(p),
      boringFilter: (p) => this._zetBoringFilter(p),
      wisselBzb: (t) => this._wisselBzb(t),
      wisFilters: () => this._wisFilters(),
      periode: (p) => this._zetPeriode(p),
    };
  }

  /** De tijd uit het filter: een vaste periode, of 'aangepast' met een eigen begin en eind. */
  _zetPeriode({ periode, van, tot } = {}) {
    if (!PERIODES.some((p) => p.id === periode)) return;
    this._periode = periode;
    this._periodeVan = isDatum(van) ? van : null;
    this._periodeTot = isDatum(tot) ? tot : null;
  }

  _zetBoringFilter(patch) {
    this._boringFilter = { ...this._boringFilter, ...patch };
  }

  _renderKaart() {
    const gekozen = this.gekozen;
    return html`
      <div class="kaartkolom">
        <!-- De hele kopbalk klapt in en uit, behalve de knop 'Kaart in apart venster'. -->
        <div class="blokkop sectiekop klikbaar" @click=${() => this._zetPaneel('kaart', !this._kaartOpen)}>
          ${this._inklapknop(this._kaartOpen, 'Kaart', () => {})}
          ${this._kaartOpen && !this._venster ? html`<span class="subkop">${this._kaartModus === 'selecteren'
            ? KADER_UITLEG
            : 'klik boringen om te vergelijken'}</span>` : ''}
          <span class="spacer"></span>
          <!-- De kaart in een eigen browservenster, voor een tweede scherm: dan krijgen de
               profielen hier de hele breedte. -->
          ${this._venster ? '' : html`
            <button class="tekstknop vensterknop" title="Open de kaart in een eigen browservenster, bijvoorbeeld op je tweede scherm. Selecteren werkt in beide richtingen."
                    @click=${(e) => { e.stopPropagation(); this._openVenster(); }}>
              ${unsafeSVG(lucideIcon(MonitorUp, 14))} Kaart in apart venster
            </button>`}
        </div>
        ${this._renderDeellocatieKeuze()}
        <!-- Ingeklapt verbergen en niet weghalen: Leaflet hangt aan dit element, en een nieuwe
             kaart zou je uitsnede en zoomstand kwijtraken. -->
        ${this._kaartOpen && this._venster ? html`
          <div class="vensterplek">
            ${unsafeSVG(lucideIcon(MonitorUp, 18))}
            <span>De kaart staat in een apart venster. Sleep dat naar je tweede scherm; selecteren daar werkt hier door.</span>
            <span class="spacer"></span>
            <button class="tekstknop" @click=${() => this._vensterRef?.focus()}>Naar het venster</button>
            <button class="tekstknop groen" @click=${() => this._sluitVenster()}>Kaart terughalen</button>
          </div>` : ''}
        ${this._vensterFout ? html`<p class="naamfout">${this._vensterFout}</p>` : ''}
        <div class="kaartvak" ?hidden=${!this._kaartOpen || this._venster}>
          <!-- Geen class-binding op deze div: Lit schrijft het hele class-attribuut opnieuw bij
               een update, en Leaflet zet zijn eigen classes op datzelfde element. -->
          <div id="kaart" class="kaart"></div>
          ${this._greep('kaartHoogte', '#kaart', 'hoogte', 'Sleep om de kaart hoger of lager te maken')}
          <!-- Het gereedschap met de iconen die elke kaart ervoor gebruikt: een kader met een
               aanwijzer om te selecteren, een handje om te verschuiven. -->
          <div class="kaartmodus">
            ${[['selecteren', 'Selecteren — slepen trekt een kader over de kaart', MousePointerSquareDashed],
               ['verplaatsen', 'Verplaatsen — slepen verschuift de kaart', Hand]].map(([id, uitleg, icoon]) => html`
              <button class=${this._kaartModus === id && !this._dlMode ? 'aan' : ''} title=${uitleg} aria-label=${uitleg}
                      aria-pressed=${this._kaartModus === id && !this._dlMode} ?disabled=${!!this._dlMode}
                      @click=${() => this._zetKaartModus(id)}>${unsafeSVG(lucideIcon(icoon, 16))}</button>`)}
            ${this.projectId ? html`
              <button class=${this._dlMode ? 'aan dl' : 'dl'} aria-pressed=${!!this._dlMode}
                      title="Deellocatie tekenen — klik de hoekpunten aan" aria-label="Deellocatie tekenen"
                      @click=${() => (this._dlMode ? this._cancelDrawing() : this._startDrawing())}>
                ${unsafeSVG(lucideIcon(Pentagon, 16))}</button>` : ''}
            <button title="Kaart in een apart venster, voor een tweede scherm" aria-label="Kaart in een apart venster"
                    ?disabled=${!!this._dlMode} @click=${() => this._openVenster()}>
              ${unsafeSVG(lucideIcon(MonitorUp, 16))}</button>
          </div>
          ${this._renderDrawingBar()}
          ${this._bewerkteDeellocatie && !this._venster ? this._deellocatieEditor(this._bewerkteDeellocatie, 'dlpaneel') : ''}
        </div>
        <div class="onderregel">
          <span class="stip ${gekozen.length ? 'aan' : ''}"></span>
          <span class="selectieregel">${gekozen.length
            ? `${meervoud(gekozen.length, 'boring', 'boringen')} geselecteerd · ${gekozen.map((b) => b.name || '?').join(', ')}`
            : 'Geen boringen geselecteerd'}</span>
          <span class="spacer"></span>
          <button class="tekstknop" ?disabled=${!this._selectie.size}
                  @click=${() => { this._selectie = new Set(); }}>Wis selectie</button>
        </div>
      </div>
    `;
  }

  /** Uitleg tijdens het tekenen, en daarna het naamveld. */
  _renderDrawingBar() {
    if (this._dlMode === 'drawing') {
      return html`
        <div class="dlbalk">
          <span>${this._dlPoints < 3
            ? html`Klik de hoekpunten van de deellocatie aan${this._dlPoints ? html` (<b>${this._dlPoints}</b> gezet)` : ''}.`
            : html`<b>${this._dlPoints}</b> punten · klik op het eerste punt of dubbelklik om af te sluiten.`}</span>
          <button ?disabled=${!this._dlPoints} @click=${() => this._drawer.undo()}>Punt terug</button>
          <button class="hoofd" ?disabled=${this._dlPoints < 3} @click=${() => this._drawer.finish()}>Afsluiten</button>
          <button @click=${() => this._cancelDrawing()}>Annuleren</button>
        </div>`;
    }
    if (this._dlMode === 'naming') {
      return html`
        <div class="dlbalk">
          <span>Naam:</span>
          <input .value=${this._dlName} maxlength="60" aria-label="Naam van de deellocatie"
                 @input=${(e) => { this._dlName = e.target.value; }}
                 @keydown=${(e) => { if (e.key === 'Enter') this._saveDrawing(); }}>
          <button class="hoofd" ?disabled=${this._dlBusy} @click=${() => this._saveDrawing()}>${this._dlBusy ? 'Opslaan…' : 'Opslaan'}</button>
          <button ?disabled=${this._dlBusy} @click=${() => this._cancelDrawing()}>Annuleren</button>
          ${this._dlError ? html`<span class="dlfout">${this._dlError}</span>` : ''}
        </div>`;
    }
    return '';
  }

  _renderProfielen() {
    const apart = !this._waterModus && this._pvVenster;
    // In een apart venster rekent _stuurProfielen; hier is dan niets te tekenen.
    const data = this._waterModus || apart ? null : this._profielData();
    return html`
      <div class="profielkolom">
        <div class="blokkop sectiekop">
          <span class="mv-caps">${this._waterModus ? 'Peilbuizen en watermonsters' : 'Boorprofielen'}</span>
          ${apart ? '' : html`<span class="subkop">${this._waterModus
            ? (this.vergrendeld ? 'de opdracht is verstuurd; de potjes liggen vast'
              : 'klik een watermonster aan: het wordt een eigen analysemonster met al zijn flessen')
            : data.subkop}</span>`}
          <span class="spacer"></span>
          <!-- De profielen in een eigen browservenster, voor een tweede scherm: hier blijven de
               kaart en de mengmonsters. Alleen bij grond; grondwater heeft geen profiel. -->
          ${this._waterModus || apart ? '' : html`
            <button class="tekstknop vensterknop" title="Open de boorprofielen in een eigen browservenster, bijvoorbeeld op je tweede scherm. Potjes aanklikken werkt daar gewoon."
                    @click=${() => this._openProfielVenster()}>
              ${unsafeSVG(lucideIcon(MonitorUp, 14))} Boorprofielen in apart venster
            </button>`}
        </div>
        ${apart ? html`
          <div class="vensterplek">
            ${unsafeSVG(lucideIcon(MonitorUp, 18))}
            <span>De boorprofielen staan in een apart venster. Sleep dat naar je tweede scherm; wat je daar aanklikt komt hier in de mengmonsters.</span>
            <span class="spacer"></span>
            <button class="tekstknop" @click=${() => this._pvRef?.focus()}>Naar het venster</button>
            <button class="tekstknop groen" @click=${() => this._sluitProfielVenster()}>Profielen terughalen</button>
          </div>` : html`
          <!-- Zoom en legenda horen bij het grondprofiel; bij grondwater is er geen profiel. -->
          ${this._waterModus ? '' : renderProfielbalk(data.balk, this._profielActies)}
          ${this._renderVergelijking(data)}`}
        ${this._pvFout ? html`<p class="naamfout">${this._pvFout}</p>` : ''}
      </div>
    `;
  }

  // ── De boorprofielen op een tweede scherm ───────────────────────────────
  //
  // Net als de kaart: deze pagina blijft de baas en stuurt na elke herteking de profielen als
  // data (zie _profielData); het venster tekent ze met dezelfde code (utils/lab-profielen.js)
  // en meldt wat je aanklikt. Twee vensters tegelijk kan: elk heeft zijn eigen kanaal.

  _openProfielVenster() {
    if (this._pvVenster) { this._pvRef?.focus(); return; }
    if (typeof BroadcastChannel === 'undefined') {
      this._pvFout = 'Deze browser kan de profielen niet in een apart venster tonen.';
      return;
    }
    const sleutel = Math.random().toString(36).slice(2, 10);
    const ref = window.open(`/profielvenster?k=${sleutel}`, `mv-lab-profielen-${sleutel}`, 'popup,width=1400,height=900');
    if (!ref) {
      this._pvFout = 'De browser hield het venster tegen. Sta pop-ups toe voor deze site en probeer het opnieuw.';
      return;
    }
    this._pvFout = '';
    this._pvRef = ref;
    this._pvKanaal = new BroadcastChannel(profielKanaalNaam(sleutel));
    this._pvKanaal.onmessage = (e) => this._vanProfielVenster(e.data || {});
    // Vangnet voor een venster dat dichtgaat zonder af te melden (een gecrasht tabblad).
    this._pvCheck = setInterval(() => { if (this._pvRef?.closed) this._sluitProfielVenster(); }, 1000);
    this._pvVenster = true;
  }

  _sluitProfielVenster() {
    clearInterval(this._pvCheck);
    this._pvCheck = null;
    if (this._pvKanaal) {
      this._pvKanaal.postMessage({ type: 'sluit' });
      this._pvKanaal.close();
    }
    this._pvKanaal = null;
    try { if (this._pvRef && !this._pvRef.closed) this._pvRef.close(); } catch (_) { /* al weg */ }
    this._pvRef = null;
    if (this._pvVenster) this._pvVenster = false;
  }

  _vanProfielVenster(m) {
    if (m.type === 'hallo') { this._stuurProfielen(); return; }
    if (m.type === 'dicht') { this._sluitProfielVenster(); return; }
    const a = this._profielActies;
    if (m.type === 'potje' && typeof m.boringId === 'string' && typeof m.potId === 'string') a.klikPotje(m.boringId, m.potId);
    if (m.type === 'boring' && typeof m.boringId === 'string') a.wisselBoring(m.boringId);
    if (m.type === 'markeer') a.markeer(typeof m.boringId === 'string' ? m.boringId : null);
    if (m.type === 'as') a.zetAs(m.modus);
    if (m.type === 'zoom') a.zoom(Number(m.stap) || 0);
    if (m.type === 'legenda') a.wisselLegenda();
    // Het filter, bediend vanuit het venster: dezelfde acties als het filterblok hier.
    if (m.type === 'filter') {
      const f = this._filterActies;
      if (m.actie === 'patch' && m.waarde && typeof m.waarde === 'object') f.patch(m.waarde);
      if (m.actie === 'boringFilter' && m.waarde && typeof m.waarde === 'object') f.boringFilter(m.waarde);
      if (m.actie === 'wisselBzb' && typeof m.waarde === 'string') f.wisselBzb(m.waarde);
      if (m.actie === 'wisFilters') f.wisFilters();
      if (m.actie === 'periode' && m.waarde && typeof m.waarde === 'object') f.periode(m.waarde);
    }
  }

  /** De profielen zoals ze nu zijn, met het filter, naar het profielvenster. */
  _stuurProfielen() {
    if (!this._pvVenster || !this._pvKanaal || this._waterModus) return;
    const data = this._profielData();
    this._pvKanaal.postMessage({ type: 'stand', ...data, filter: this._filterModel(this.inPeriode) });
  }

  /** Een potje uit een mengmonster halen (het kruisje in de kolom rechts). */
  _haalPotjeUit(code, id) {
    if (this.vergrendeld) return;
    this._mengmonsters = this._mengmonsters.map((m) => (m.code === code ? { ...m, potjes: m.potjes.filter((p) => p.id !== id) } : m));
    this._meldMengmonsters();
  }

  /**
   * De mengmonsters als kolom rechts naast de veldgegevens, zoals voor het herontwerp van
   * oktober. Daarna stonden ze als knopjes in een balk boven de profielen ('Potjes gaan naar'),
   * met de waarschuwingen van alleen het actieve mengmonster eronder: verstopt, en bij het
   * scrollen door de profielen uit beeld. Hier heeft elk mengmonster een eigen kaartje met zijn
   * potjes en zijn eigen waarschuwingen; het actieve (waar je potjes in klikt) valt op. De kolom
   * schuift mee met de pagina.
   */
  _renderMandjekolom() {
    const mm = this._mengmonsters;
    const ingedeeld = mm.reduce((n, m) => n + m.potjes.length, 0);
    const inVeld = this.overzichten.reduce((n, o) => n + o.pots.length, 0);
    const water = this._waterModus;
    const [een, veel] = water ? ['analysemonster', 'analysemonsters'] : ['mengmonster', 'mengmonsters'];
    const naarPakketten = (e) => { e.preventDefault(); this.dispatchEvent(new CustomEvent('naar-pakketten', { bubbles: true, composed: true })); };
    return html`
      <aside class="mmzij" aria-label=${water ? 'Analysemonsters' : 'Mengmonsters'}>
        <!-- Donkere kop: dit is de enige tegel waarin je iets maakt in plaats van kiest. -->
        <div class="mmhead">
          <div class="mmtitel">
            <span class="mmeyebrow">Samenstellen</span>
            <strong>${water ? 'Analysemonsters' : 'Mengmonsters'}</strong>
          </div>
          <span class="mmtelling">${meervoud(mm.length, een, veel)} · ${meervoud(inVeld, 'potje', 'potjes')} in het veld</span>
        </div>

        <div class="mmlijf">
          ${!mm.length ? html`
            <div class="mmleeg">
              <strong>Nog geen ${veel}</strong>
              <span>${water
                ? 'Klik een watermonster aan: het wordt een eigen analysemonster met al zijn flessen.'
                : 'Maak er een aan en klik daarna potjes aan in de boorprofielen. Potjes uit meerdere boringen samen vormen één mengmonster.'}</span>
            </div>` : mm.map((m) => this._renderMandjekaart(m))}
          ${this._hernoemt && this._hernoemFout ? html`<p class="naamfout">${this._hernoemFout}</p>` : ''}

          ${this.vergrendeld || water ? '' : this._nieuwNaam != null ? html`
            <div class="mmnieuwnaam">
              <input class="naamveld nieuwveld" .value=${this._nieuwNaam} maxlength="40" aria-label="Naam van het nieuwe mengmonster"
                     @input=${(e) => { this._nieuwNaam = e.target.value; this._nieuwFout = ''; }}
                     @keydown=${(e) => {
                       if (e.key === 'Enter') { e.preventDefault(); this._bevestigNieuw(e.target.value); }
                       if (e.key === 'Escape') this._annuleerNieuw();
                     }}>
              <button class="tekstknop groen" @click=${() => this._bevestigNieuw(this._nieuwNaam)}>Aanmaken</button>
              <button class="tekstknop" @click=${() => this._annuleerNieuw()}>Annuleren</button>
            </div>
            ${this._nieuwFout ? html`<p class="naamfout">${this._nieuwFout}</p>` : ''}` : html`
            <button class="mmnieuw" @click=${() => this._startNieuw()}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2.6" stroke-linecap="round"><path d="M12 5v14M5 12h14"></path></svg>
              Mengmonster toevoegen
            </button>`}
          <p class="mmvoet">${ingedeeld} van ${meervoud(inVeld, 'potje', 'potjes')} ingedeeld. Welke analyses
             erop komen kies je ${html`<a href="#" @click=${naarPakketten}>bij de pakketten</a>`}.</p>
        </div>
      </aside>`;
  }

  _openNotitie(m) {
    this._notitieVoor = m.code;
    this._notitieTekst = m.notitie || '';
    this.updateComplete.then(() => {
      const d = this.renderRoot.querySelector('dialog.notitievenster');
      if (d && !d.open) d.showModal();
      d?.querySelector('textarea')?.focus();
    });
  }

  _bewaarNotitie() {
    const code = this._notitieVoor;
    this._notitieVoor = null;
    const m = this._mengmonsters.find((x) => x.code === code);
    const tekst = this._notitieTekst.trim();
    if (m && (m.notitie || '') !== tekst) this.zetNotitie(code, tekst);
  }

  _renderNotitievenster() {
    const code = this._notitieVoor;
    if (!code) return '';
    const sluit = () => { this._notitieVoor = null; };
    return html`
      <dialog class="notitievenster" @cancel=${(e) => { e.preventDefault(); sluit(); }}
              @click=${(e) => { if (e.target === e.currentTarget) sluit(); }}>
        <header><strong>Notitie bij ${code}</strong>
          <button class="kolomweg" title="Sluiten" @click=${sluit}>×</button></header>
        <textarea rows="5" .value=${this._notitieTekst} ?readonly=${this.vergrendeld}
                  placeholder="Bijvoorbeeld waarom juist deze potjes samen gaan"
                  @input=${(e) => { this._notitieTekst = e.target.value; }}></textarea>
        <div class="notitieknoppen">
          <button class="tekstknop" @click=${sluit}>${this.vergrendeld ? 'Sluiten' : 'Annuleren'}</button>
          ${this.vergrendeld ? '' : html`<button class="tekstknop groen" @click=${() => this._bewaarNotitie()}>Notitie opslaan</button>`}
        </div>
      </dialog>`;
  }

  _renderMandjekaart(m) {
    const actief = m.code === this._actief;
    const waarsch = this.waarschuwingen(m.code);
    const kies = () => this.kiesMengmonster(m.code);
    return html`
      <!-- De hele kaart schakelt: de groene rand zegt welk mengmonster de potjes opvangt. -->
      <div class="mmkaart ${actief ? 'actief' : ''}" tabindex="0" role="button" aria-pressed=${actief}
           title=${actief ? 'Potjes die je aanklikt komen in dit mengmonster' : `Klik om verder te vullen met ${m.code}`}
           @click=${(e) => { if (!e.target.closest('button, input')) kies(); }}
           @keydown=${(e) => { if ((e.key === 'Enter' || e.key === ' ') && !e.target.closest('input')) { e.preventDefault(); kies(); } }}>
        <div class="mmkaartkop">
          ${this._hernoemt === m.code ? html`
            <input class="naamveld" .value=${m.code} maxlength="40" aria-label="Naam van het mengmonster"
                   @keydown=${(e) => {
                     if (e.key === 'Enter') { e.preventDefault(); this._bewaarHernoem(m.code, e.target.value); }
                     if (e.key === 'Escape') { this._hernoemt = null; this._hernoemFout = ''; }
                   }}
                   @blur=${(e) => this._bewaarHernoem(m.code, e.target.value)}>` : html`
            <!-- Op de naam klikken = de naam aanpassen; ergens anders op de kaart = dit mengmonster vullen. -->
            ${this.vergrendeld ? html`<strong class="mmcode">${m.code}</strong>` : html`
              <button class="mmcode mmnaamknop" title="Klik om de naam aan te passen"
                      @click=${() => { this._openHernoem(m.code); }}>${m.code}</button>`}`}
          <span class="mmaantal">${meervoud(m.potjes.length, 'potje', 'potjes')}</span>
          <!-- Grijs zonder notitie, groen met: zo zie je aan de lijst welk mengmonster een
               toelichting heeft zonder ze te openen. Dezelfde notitie als bij Pakketten. -->
          ${this.vergrendeld && !m.notitie ? '' : html`
            <button class="mmicoon ${m.notitie ? 'gevuld' : ''}"
                    title=${m.notitie ? `Notitie: ${m.notitie}` : 'Notitie toevoegen — bijvoorbeeld waarom juist deze potjes samen gaan'}
                    aria-label=${`Notitie bij ${m.code}`}
                    @click=${() => this._openNotitie(m)}>${unsafeSVG(lucideIcon(StickyNote, 13))}</button>`}
          ${this.vergrendeld ? '' : html`
            <button class="mmweg" title=${m.potjes.length ? 'Verwijderen (de potten komen weer vrij)' : 'Mengmonster verwijderen'}
                    aria-label=${`${m.code} verwijderen`} @click=${() => this._vraagVerwijder(m)}>&times;</button>`}
        </div>
        ${waarsch.length ? html`
          <ul class="mmkaartwaarsch" title="Mengregels die ${m.code} breekt">
            ${waarsch.map((w) => html`<li>${w}</li>`)}
          </ul>` : ''}
        <div class="mmchips">
          ${m.potjes.map((p) => html`
            <button class="mmchip" ?disabled=${this.vergrendeld}
                    title=${`${p.boringNaam} · ${p.van}–${p.tot} cm${p.geroerd === false ? ' · steekbus' : ''}${this.vergrendeld ? '' : ' — klik om eruit te halen'}`}
                    @click=${() => this._haalPotjeUit(m.code, p.id)}>
              ${p.naam}${this.vergrendeld ? '' : html`<span class="chipweg">&times;</span>`}
            </button>`)}
          ${!m.potjes.length ? html`<span class="mmleegchip">Klik nu potjes aan in de boorprofielen.</span>` : ''}
        </div>
      </div>`;
  }

  /**
   * De gekozen boringen naast elkaar, zoals in het ontwerp: per laag een rij, met de
   * bovengrens, het profiel in de textuur van de grondsoort met de potjes erin, en de
   * beschrijving.
   *
   * Alle profielen delen één diepte-as: dezelfde diepte staat in elke kolom op dezelfde
   * hoogte, zodat je de boringen naast elkaar kunt vergelijken. Basis is 56 px per meter, maar
   * een laag wordt nooit lager dan één regel en altijd hoog genoeg voor zijn potjes. Moet een
   * dunne laag groeien, dan groeit dat dieptetraject in álle kolommen mee. Hoogteverschillen
   * tussen boringen staan in de kop (maaiveld, einddiepte, GWS).
   */
  /**
   * Alles wat de boorprofielen tonen, als gewone data (zie utils/lab-profielen.js): voor de
   * profielen hier en voor het profielvenster op een tweede scherm. Maakt ook de texturen aan;
   * `defs` komt pas na de kolommen en de legenda, want dan weet je welke patronen nodig zijn.
   */
  _profielData() {
    // Texturen per laag zijn instelbaar per bedrijf. Eén verzameling voor legenda en grafiek.
    this._tex = createTextureDefs('mmt');
    const tex = this._tex;
    const columns = this.gekozen.map((b) => this._kolomData(b));
    // Op NAP: het hoogste maaiveld bovenaan, de rest zakt zoveel cm als hun maaiveld lager ligt.
    // Een boring zonder maaiveldhoogte zetten we bovenaan; dat staat eronder.
    const nap = this._asModus === 'nap';
    const hoogtes = columns.map((k) => k.z).filter((z) => z != null);
    const top = hoogtes.length ? Math.max(...hoogtes) : 0;
    for (const k of columns) k.offset = nap && k.z != null ? Math.round((top - k.z) * 100) : 0;
    const ticks = applySharedDepthAxis(columns, this._schaal);
    // De legenda volgt de standaard van de gekozen boringen; zijn dat er twee, dan NEN 6693.
    const standaarden = new Set(this.gekozen.map((b) => b._standard || null));
    const std = standaarden.size === 1 ? [...standaarden][0] : null;
    const legenda = soilLegend(std, getCompanyTextures()).map(([t, label]) => [tex.ref(t), label]);
    const maten = kolomMaten(this._schaal);
    const actief = this._mengmonsters.find((m) => m.code === this._actief);
    return {
      subkop: this.vergrendeld ? 'de opdracht is verstuurd; de potjes liggen vast'
        : `klik potjes aan voor ${actief?.code || 'een nieuw mengmonster'}`,
      balk: {
        legendaOpen: !!this._legenda, legenda, asModus: this._asModus, procent: schaalProcent(this._schaal),
        kleinste: this._schaal === PROFIEL_SCHALEN[0], grootste: this._schaal === PROFIEL_SCHALEN.at(-1),
      },
      profielen: {
        nap, top, ticks, vergrendeld: !!this.vergrendeld, kolomB: maten.kolom, balkB: maten.balk,
        zonderZ: nap ? columns.filter((k) => k.z == null).map((k) => k.naam) : [],
        kolommen: columns.map((k) => ({
          id: k.boring.id, naam: k.naam, meta: k.meta, eind: k.eind,
          lagen: k.lagen.map((l) => ({
            sleutel: l.sleutel, van: l.van, tot: l.tot, hoogte: l.hoogte, gapBefore: l.gapBefore, fill: l.fill,
            beschrijving: l.beschrijving, meta: l.meta, chips: l.chips, past: l.past, geenMonster: l.geenMonster,
            potjes: l.potjes.map((pt) => ({
              id: pt.id, naam: pt.naam, van: pt.van, tot: pt.tot, geroerd: pt.geroerd, erin: pt.erin,
              bezetDoor: pt.bezetDoor, gedimd: pt.gedimd, tip: this._potjeTip(pt),
            })),
          })),
        })),
      },
      defs: tex.defs(),
    };
  }

  /** Wat je in de profielen doet, hier of in het profielvenster. */
  get _profielActies() {
    const boringVan = (id) => this.gekozen.find((b) => b.id === id);
    return {
      wisselLegenda: () => { this._legenda = !this._legenda; },
      zetAs: (modus) => this._zetAsModus(modus === 'nap' ? 'nap' : 'maaiveld'),
      zoom: (stap) => this._zetSchaal(stap > 0 ? 1 : -1),
      klikPotje: (boringId, potId) => {
        const b = boringVan(boringId);
        const m = b && (b.monsters || []).find((x) => !x.deletedAt && potIdOf(b, x) === potId);
        if (m) this._klikPotje(b, m, Number(m.upperDepth) || 0, Number(m.lowerDepth) || 0);
      },
      wisselBoring: (boringId) => { const b = boringVan(boringId); if (b) this._wisselBoring(b); },
      markeer: (boringId) => this._markeerOpKaart(boringId),
      bzbMeer: (e, naam, laag) => { this._bzbKaart = bzbKaartVan(e, naam, laag); },
    };
  }

  _renderVergelijking(data) {
    if (this._waterModus) return this._renderWaterKolommen();
    const m = data.profielen;
    return html`
      ${m.kolommen.length ? html`
        <div class="vergelijk" @mousemove=${this._tipBeweeg} @mouseleave=${this._tipWeg}
             style=${`--kolom-b:${m.kolomB}px; --balk-b:${m.balkB}px`}>
          ${renderProfielKolommen(m, this._profielActies)}
        </div>
        ${m.zonderZ.length ? html`<p class="asnoot">Zonder maaiveldhoogte, bovenaan gezet: ${m.zonderZ.join(', ')}</p>` : ''}
        ${this._greep('profielHoogte', '.vergelijk', 'hoogte', 'Sleep om het profielvak hoger of lager te maken')}` : html`
        <div class="leegvlak">Klik een of meer boringen op de kaart om hun profielen naast elkaar te zien.</div>`}
      <svg class="defs" width="0" height="0" aria-hidden="true">${unsafeSVG(data.defs)}</svg>
    `;
  }

  /**
   * Alles wat één boringkolom nodig heeft: de lagen met hun hoogte en of ze aan het filter
   * voldoen, en per laag de potjes.
   *
   * Een potje hoort bij de laag waar zijn midden in valt. Zo staat elk potje precies één keer
   * in beeld, ook als het over een laaggrens heen is gestoken. Valt het midden in een gat
   * tussen twee beschreven lagen, dan bij de dichtstbijzijnde.
   */
  _kolomData(boring) {
    const data = buildBoorprofielData(boring) || { layers: [] };
    const model = BoringModel.fromFlat(boring);
    const ov = this._overzicht(boring);
    const lagen = data.layers.map((l, i) => {
      const van = Number(l.from) || 0, tot = Number(l.to) || 0;
      const lm = model.layers?.[i];
      const ovl = ov.layers[i];
      const lt = layerTextures(l, data.standard, data.textures);
      // Zonder grondsoort de BZB waar de laag uit bestaat (asfalt, puin); pas daarna 'onbekend'.
      const vulling = (l.bzb || []).filter((b) => b.fillsLayer).map((b) => b.label).join(' en ');
      const beschrijving = [l.soilLabel || vulling || 'grondsoort onbekend',
        ...(l.admix || []).map((a) => [a.grad, a.label].filter(Boolean).join(' '))].filter(Boolean).join(', ');
      const waarneming = [ovl?.odor, ovl?.oil].filter(Boolean);
      return {
        sleutel: `${boring.id}:${i}`, van, tot,
        fill: this._tex.ref(lt.main.t),
        beschrijving: beschrijving.charAt(0).toUpperCase() + beschrijving.slice(1),
        meta: [lm?.kleurLabel || '', lm?.mediaanLabel ? `Ø ${lm.mediaanLabel}` : '', `${cmNaarM(van)}–${cmNaarM(tot)} m`]
          .filter(Boolean).join(' · '),
        chips: [...(lm?.bzbLabels || []), ...waarneming].map((naam) => ({ naam, ...bzbStijl(naam) })),
        past: this._laagPast(boring, i),
        geenMonster: !(ovl?.pots.length),
        potjes: [],
      };
    });

    const monsters = (boring.monsters || []).filter((m) => !m.deletedAt)
      .sort((a, b) => (Number(a.upperDepth) || 0) - (Number(b.upperDepth) || 0));
    // Potjes zonder beschreven lagen: dan één lege laag, anders zou je ze niet kunnen kiezen.
    if (!lagen.length && monsters.length) {
      lagen.push({
        sleutel: `${boring.id}:-`, van: 0, tot: Math.max(...monsters.map((m) => Number(m.lowerDepth) || 0)),
        fill: '#fff', beschrijving: 'Geen lagen beschreven', meta: '', chips: [], past: true, geenMonster: false, potjes: [],
      });
    }
    for (const m of monsters) {
      const van = Number(m.upperDepth) || 0, tot = Number(m.lowerDepth) || 0;
      const midden = (van + tot) / 2;
      const afstand = (l) => (midden < l.van ? l.van - midden : midden >= l.tot ? midden - l.tot : 0);
      const laag = lagen.reduce((best, l) => (!best || afstand(l) < afstand(best) ? l : best), null);
      if (!laag) continue;
      const id = potIdOf(boring, m);
      const zit = this.zitIn(id);
      laag.potjes.push({
        id, item: m, van, tot,
        naam: m.name || m.identificatie || '—',
        geroerd: m.geroerd !== false,
        erin: !!zit && zit === this._actief,
        bezetDoor: zit && zit !== this._actief ? zit : null,
        verpakking: ov.pots.find((x) => x.id === id)?.packagingLabel || '',
        // Andere verpakking, of buiten het monstertraject: blijft klikbaar, maar treedt terug.
        gedimd: !potPassesPackaging({ packaging: packagingOf(m) }, this._filter)
          || !potInDepthRange({ from: van, to: tot }, this._filter),
      });
    }
    // Wat een laag minstens nodig heeft; de echte hoogte volgt uit de gedeelde diepte-as
    // (zie applySharedDepthAxis). Alleen de potjes tellen: die moet je kunnen aanklikken. De
    // tekst past zich aan de laag aan en staat voluit in de tooltip -- liet je die ook ruimte
    // eisen, dan werd de as met een paar boringen naast elkaar meters lang.
    for (const l of lagen) {
      l.minHeight = Math.max(MIN_LAAG_PX, l.potjes.length * POTJE_PX + 4);
    }

    const diepte = Math.max(0, ...lagen.map((l) => l.tot));
    const heeftZ = boring.z !== null && boring.z !== undefined && boring.z !== '' && Number.isFinite(Number(boring.z));
    const sublocations = this._sublocationsOf(boring);
    return {
      boring,
      naam: boring.name || 'Naamloos',
      meta: [
        // Eerst: mengmonsters stel je per deellocatie samen, dus dat wil je als eerste lezen.
        sublocations.map((s) => s.name).join(' + '),
        heeftZ ? `Maaiveld ${Number(boring.z).toFixed(2).replace('.', ',')} m +NAP` : 'Maaiveld onbekend',
        `einddiepte ${cmNaarM(diepte)} m –mv`,
        ov.gws != null ? `GWS ${cmNaarM(ov.gws)} m` : '',
      ].filter(Boolean).join(' · '),
      lagen,
      eind: cmNaarM(diepte),
      z: heeftZ ? Number(boring.z) : null,
    };
  }

  /**
   * Grondwater: per gekozen boring de peilbuis met zijn filter en de watermonsters als tegels.
   * Een tegel aanklikken maakt er een analysemonster van (of haalt het weer weg).
   */
  _renderWaterKolommen() {
    const gekozen = this.gekozen;
    if (!gekozen.length) {
      return html`<div class="leegvlak">Klik een of meer boringen met een peilbuis op de kaart om hun watermonsters te zien.</div>`;
    }
    const m = (v) => (v === null || v === undefined || v === '' ? null : v);
    return html`
      <div class="watervlak">
        ${gekozen.map((b) => {
          const lijst = watermonstersOf(b);
          const dl = this._sublocationsOf(b).map((s) => s.name).join(' + ');
          return html`
            <div class="waterkolom">
              <div class="kolomkop"><strong>Boring ${b.name || 'Naamloos'}</strong></div>
              <div class="kolommeta">${[dl, `${(b.peilbuizen || []).filter((p) => !p.deletedAt).length} peilbuis`].filter(Boolean).join(' · ')}</div>
              ${lijst.length ? lijst.map((w) => {
                const zit = this._waterIn(w);
                const pb = w.peilbuis;
                const metingen = [m(pb?.ph) != null ? `pH ${String(pb.ph).replace('.', ',')}` : '',
                  m(pb?.ec) != null ? `EC ${pb.ec} µS/cm` : '',
                  m(pb?.temperature) != null ? `${String(pb.temperature).replace('.', ',')} °C` : ''].filter(Boolean).join(' · ');
                return html`
                  <button class="watertegel ${zit ? 'erin' : ''}" ?disabled=${this.vergrendeld || !w.potjes.length}
                          title=${zit ? `In ${zit.code} · klik om dit analysemonster weg te halen` : 'Klik: een eigen analysemonster met deze flessen'}
                          @click=${() => this._klikWatermonster(w)}>
                    <span class="watertitel">${w.wm.name || 'Watermonster'}${zit ? html` <span class="waterin">→ ${zit.code}</span>` : ''}</span>
                    <span class="watermeta">${[w.wm.date ? new Date(w.wm.date).toLocaleDateString('nl-NL') : '',
                      w.filter ? `filter ${cmNaarM(w.filter.van)}–${cmNaarM(w.filter.tot)} m –mv` : 'filterdiepte onbekend',
                      metingen].filter(Boolean).join(' · ')}</span>
                    <span class="flessen">
                      ${w.potjes.map((p) => html`
                        <span class="fles ${p.barcode ? '' : 'zonder'}" title=${p.barcode ? `Barcode ${p.barcode}` : 'Geen barcode'}>
                          ${p.naam.replace(/^.* fles /, 'fles ')}${p.gefiltreerd ? html`<b>gefiltreerd</b>` : ''}
                          <code>${p.barcode || 'geen barcode'}</code>
                        </span>`)}
                    </span>
                  </button>`;
              }) : html`<div class="leegregel">Geen watermonsters bij deze boring.</div>`}
            </div>`;
        })}
      </div>`;
  }

  /**
   * De tooltip van een potje: benoemde velden (Monster, Diepte, Monstername, Verpakking) en
   * daaronder wat een klik doet, als een duidelijk gekleurde actie. Zie data-tip-rows in
   * utils/hover-tip.js; `tekst` is dezelfde inhoud als platte tekst.
   */
  _potjeTip(p) {
    const rows = [
      ['Monster', p.naam],
      ['Diepte', `${p.van}–${p.tot} cm`],
      ['Monstername', p.geroerd ? 'Geroerd (schep)' : 'Ongeroerd (steekbus)'],
      ...(p.verpakking ? [['Verpakking', p.verpakking]] : []),
    ];
    const action = this.vergrendeld ? null
      : p.bezetDoor ? { text: `Zit al in ${p.bezetDoor}`, kind: 'info' }
      : p.erin ? { text: `Klik om uit ${this._actief} te halen`, kind: 'remove' }
      : { text: `+ Klik om toe te voegen aan ${this._actief || 'een nieuw mengmonster'}`, kind: 'add' };
    return {
      rijen: { rows, action },
      tekst: [...rows.map(([l, w]) => `${l}: ${w}`), action?.text].filter(Boolean).join('\n'),
    };
  }

  static styles = css`${unsafeCSS(base)}
    ${unsafeCSS(PROFIELEN_CSS)}
    :host { display:block; }
    :host([hidden]) { display:none; }
    .defs { position:absolute; width:0; height:0; }
    .spacer { flex:1; min-width:8px; }
    .mv-caps { font-size:.66rem; font-weight:900; letter-spacing:.08em; color:var(--mv-text-sub); }
    .subkop { font-size:.72rem; color:var(--mv-text-muted); }
    .paneel { border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface); min-width:0;
              container-type:inline-size; container-name:paneel; }
    /* Veldgegevens links, mengmonsters rechts; de tegel zoals vóór het herontwerp van oktober. */
    .werkruimte { display:grid; grid-template-columns:minmax(0,1fr) 300px; gap:16px; align-items:start; }
    .mmzij { position:sticky; top:12px; max-height:calc(100vh - 24px); overflow:auto;
             border:1px solid var(--mv-border); border-radius:12px; background:var(--mv-surface);
             box-shadow:0 2px 10px rgba(16,28,18,.05); }
    .mmhead { display:flex; align-items:flex-start; gap:10px; padding:14px 16px 13px; background:#22371f; }
    .mmtitel { min-width:0; }
    .mmeyebrow { display:block; font-size:.62rem; font-weight:800; letter-spacing:.1em; text-transform:uppercase; color:#a9c69f; }
    .mmtitel strong { display:block; margin-top:2px; font-size:.95rem; font-weight:800; color:#fff; }
    .mmtelling { margin-left:auto; max-width:130px; text-align:right; font-size:.68rem; font-weight:700; color:#cfe0c9; }
    .mmlijf { padding:12px 14px 14px; }
    .mmleeg { padding:16px 14px; border:1.5px dashed var(--mv-border); border-radius:10px; text-align:center; }
    .mmleeg strong { display:block; font-size:.8rem; color:var(--mv-text); }
    .mmleeg span { display:block; margin-top:5px; font-size:.72rem; color:var(--mv-text-muted); line-height:1.5; }
    .mmkaart { border:1.5px solid var(--mv-border); border-radius:10px; padding:9px 11px 10px; margin-bottom:9px; cursor:pointer; }
    .mmkaart:hover:not(.actief) { border-color:#b7c9b1; background:var(--mv-bg); }
    .mmkaart:focus-visible { outline:2px solid var(--mv-primary); outline-offset:2px; }
    .mmkaart.actief { border-color:var(--mv-primary); background:rgba(75,157,53,.05); }
    .mmkaartkop { display:flex; align-items:center; gap:6px; }
    .mmcode { flex:1; min-width:0; font-size:.82rem; font-weight:800; color:var(--mv-text); }
    .mmkaartkop .naamveld { flex:1; min-width:0; }
    .mmaantal { text-align:right; font-size:.64rem; color:var(--mv-text-muted); white-space:nowrap; }
    .mmicoon { display:flex; padding:2px; border:none; border-radius:5px; background:none; color:var(--mv-border-strong, #c3ccc4); cursor:pointer; }
    .mmicoon:hover { background:var(--mv-bg); color:var(--mv-text-sub); }
    .mmicoon.gevuld { color:var(--mv-primary); }
    .mmnaamknop { border:none; background:none; padding:0; text-align:left; font:inherit; font-size:.82rem; font-weight:800;
                  color:var(--mv-text); cursor:text; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .mmnaamknop:hover { text-decoration:underline dotted; text-underline-offset:3px; }
    .notitievenster { width:min(440px, calc(100vw - 32px)); padding:14px 16px 16px; border:none; border-radius:12px;
                      background:var(--mv-surface); color:var(--mv-text); box-shadow:0 24px 60px rgba(0,0,0,.25); }
    .notitievenster::backdrop { background:rgba(15,23,42,.45); }
    .notitievenster header { display:flex; align-items:center; gap:10px; margin-bottom:10px; }
    .notitievenster header strong { flex:1; font-size:.95rem; }
    .notitievenster textarea { width:100%; box-sizing:border-box; padding:8px 10px; border-radius:8px; border:1px solid var(--mv-border);
                               background:var(--mv-bg); color:var(--mv-text); font:inherit; font-size:.82rem; resize:vertical; }
    .notitieknoppen { display:flex; align-items:center; justify-content:flex-end; gap:12px; margin-top:10px; }
    .mmweg { border:none; background:none; font-size:1rem; line-height:1; color:var(--mv-text-muted); cursor:pointer; padding:0 2px; }
    .mmweg:hover { color:var(--mv-danger); }
    .mmkaartwaarsch { margin:5px 0 0; padding:4px 6px 4px 18px; border-radius:4px; background:var(--mv-danger-bg);
                      color:var(--mv-danger); font-size:.62rem; font-weight:700; line-height:1.4; }
    .mmchips { display:flex; flex-wrap:wrap; gap:4px; margin-top:7px; }
    .mmchip { display:inline-flex; align-items:center; gap:3px; padding:2px 7px; border-radius:5px;
              border:1px solid #dde8da; background:#f7faf7; color:#2f6122;
              font:inherit; font-size:.64rem; font-weight:800; cursor:pointer; }
    .mmchip:hover:not(:disabled) { border-color:var(--mv-danger); color:var(--mv-danger); }
    .mmchip:disabled { cursor:default; }
    .chipweg { font-size:.8rem; line-height:1; }
    .mmleegchip { font-size:.66rem; color:var(--mv-text-muted); line-height:1.45; }
    .mmnieuw { display:flex; align-items:center; justify-content:center; gap:8px; width:100%;
               margin-top:12px; padding:10px 15px; border-radius:9px; border:1.5px dashed #b7c9b1;
               background:var(--mv-surface); color:var(--mv-primary);
               font:inherit; font-size:.76rem; font-weight:800; cursor:pointer; }
    .mmnieuw:hover { border-color:var(--mv-primary); background:rgba(75,157,53,.05); }
    .mmnieuwnaam { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-top:12px; }
    .mmvoet { margin:10px 0 0; font-size:.64rem; color:var(--mv-text-muted); line-height:1.5; }
    .mmvoet a { color:var(--mv-primary); font-weight:700; }
    @media (max-width: 1100px) {
      .werkruimte { grid-template-columns:minmax(0,1fr); }
      .mmzij { position:static; max-height:none; order:-1; }
    }

    .kop { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:14px 20px 12px;
           background:#22371f; border-radius:11px 11px 0 0; }
    .kop .mv-caps { color:#a9c69f; }
    .kop .subkop { color:#cfe0c9; }
    .filterkolom { border-bottom:1px solid var(--mv-border-soft); }
    .filterkolom > .sectiekop { margin:0; }
    .filterkolom .filterblok { border-bottom:none; padding-bottom:8px; }

    /* ── Filter ──────────────────────────────────────────────────────────── */
    /* Compact: de filters zijn hulpmiddel, de kaart en de profielen zijn waar je werkt. */
    ${unsafeCSS(FILTERBLOK_CSS)}
    .tekstknop.sterk { color:var(--mv-text); }

    /* ── Kaart boven de profielen ────────────────────────────────────────────
       De kaart over de volle breedte: je klikt er boringen op aan, en een smalle plattegrond
       bij twintig boringen is een kluwen stippen. De profielen eronder krijgen dan ook de hele
       breedte, zodat er meer boringen naast elkaar passen. */
    .werkvlak { min-width:0; }
    .kaartkolom { padding:16px 20px 18px; border-bottom:1px solid var(--mv-border-soft); min-width:0; }
    .profielkolom { padding:16px 20px 18px; min-width:0; }
    .blokkop { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-bottom:10px; }
    /* Filters, Kaart en Boorprofielen: een lichtgrijze balk over de hele breedte, zodat je de
       vlakken in één oogopslag uit elkaar houdt. */
    .sectiekop { margin:-16px -20px 12px; padding:9px 20px; min-height:22px; background:color-mix(in srgb, var(--mv-text) 5%, var(--mv-surface));
                 border-bottom:1px solid var(--mv-border-soft); }
    .sectiekop.klikbaar { cursor:pointer; user-select:none; }
    .sectiekop.klikbaar:hover .mv-caps { color:var(--mv-primary); }

    ${unsafeCSS(leafletCss)}
    .kaartvak { position:relative; }
    .kaartvak[hidden] { display:none; }
    /* Hoog genoeg om twintig boringen uit elkaar te houden; met de greep eronder zelf in te stellen. */
    .kaart { height:var(--kaart-hoogte, min(520px, 58vh)); border:1px solid var(--mv-border); border-radius:10px; overflow:hidden; }

    /* Grepen om de vlakken groter of kleiner te slepen. */
    .greep { touch-action:none; user-select:none; position:relative; }
    .greep::after { content:''; position:absolute; border-radius:3px; background:var(--mv-border); transition:background .12s ease; }
    .greep:hover::after, .greep.sleept::after { background:var(--mv-primary); }
    .greep.hoogte { height:12px; cursor:row-resize; }
    .greep.hoogte::after { left:50%; top:4px; width:44px; height:4px; transform:translateX(-50%); }
    .greep.breedte { display:none; cursor:col-resize; }
    .greep.breedte::after { top:50%; left:3px; width:4px; height:44px; transform:translateY(-50%); }

    .vensterknop { display:inline-flex; align-items:center; gap:5px; }
    /* Grondwater: per boring de watermonsters als tegels. */
    .watervlak { display:flex; gap:20px; overflow:auto; max-height:var(--profiel-hoogte, 78vh); padding:6px 2px; align-items:flex-start; }
    .waterkolom { flex:0 0 300px; min-width:0; display:flex; flex-direction:column; gap:8px; }
    .watertegel { display:flex; flex-direction:column; align-items:stretch; gap:4px; text-align:left; padding:10px 12px;
                  border:1.5px solid var(--mv-border); border-radius:10px; background:var(--mv-surface); color:var(--mv-text);
                  font:inherit; cursor:pointer; }
    .watertegel:hover:not(:disabled) { border-color:var(--mv-primary); }
    .watertegel.erin { border-color:var(--mv-primary); background:var(--mv-primary-tint); }
    .watertegel:disabled { cursor:default; opacity:.6; }
    .watertitel { font-size:.82rem; font-weight:900; }
    .waterin { color:var(--mv-primary-hover); font-weight:800; }
    .watermeta { font-size:.68rem; color:var(--mv-text-muted); }
    .flessen { display:flex; flex-direction:column; gap:3px; margin-top:4px; }
    .fles { display:flex; align-items:baseline; gap:6px; font-size:.72rem; font-weight:700; }
    .fles b { font-size:.62rem; font-weight:800; padding:0 5px; border-radius:4px; background:#e8f0fb; color:#1c5fa8; }
    .fles code { margin-left:auto; font-size:.68rem; color:var(--mv-text-sub); }
    .fles.zonder code { color:var(--mv-danger); }
    /* Een analysemonster met zijn knopjes: potlood (naam) en kruis (verwijderen). */
    .naargroep { display:inline-flex; align-items:center; gap:1px; }
    .chipactie { display:inline-flex; align-items:center; justify-content:center; width:20px; height:22px; padding:0;
                 border:none; background:none; color:var(--mv-text-muted); border-radius:5px; cursor:pointer; opacity:.55; }
    .naargroep:hover .chipactie, .naargroep.aan .chipactie, .dlbeheer .chipactie { opacity:1; }
    .chipactie:hover { color:var(--mv-primary); background:var(--mv-surface); }
    .chipactie.weg:hover { color:var(--mv-danger); }
    .nieuwbalk { display:inline-flex; align-items:center; gap:8px; }
    .nieuwveld { width:140px; }
    .dlbeheer { display:inline-flex; align-items:center; gap:1px; }
    .dlchip.beheer { gap:4px; align-items:center; }
    .dlnaamveld { width:120px; border-color:#c2410c; }
    /* Het bewerkvenster van een deellocatie rechts over de kaart, zoals op de projectkaart. */
    .dlpaneel { position:absolute; top:52px; right:10px; bottom:10px; width:min(420px, calc(100% - 70px)); z-index:1001; }
    .dlvenster { width:min(460px, calc(100vw - 32px)); height:min(640px, calc(100vh - 48px)); padding:0; border:none;
                 background:none; overflow:visible; }
    .dlvenster:focus, .dlvenster:focus-visible { outline:none; }
    .dlvenster::backdrop { background:rgba(15,23,42,.45); }
    .dlvenster sublocation-editor { height:100%; }
    .dlbeheerfout { color:var(--mv-danger); font-size:.72rem; font-weight:700; }
    .legendaknop.inklap { gap:6px; color:var(--mv-text-sub); }
    .legendaknop.inklap:hover, .legendaknop.inklap:hover .mv-caps { color:var(--mv-primary); }
    .actieflabel { font-size:.64rem; font-weight:800; padding:1px 6px; border-radius:5px;
                   background:var(--mv-primary-tint); color:var(--mv-primary-hover); }
    .dlrij { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-bottom:10px; }
    .dlchip { display:inline-flex; align-items:baseline; gap:5px; padding:4px 10px; border-radius:7px;
              border:1.5px solid #f0c7b0; background:#fff7f2; color:#9a3412;
              font:inherit; font-size:.74rem; font-weight:800; cursor:pointer; }
    .dlchip:hover:not(:disabled) { border-color:#c2410c; }
    .dlchip .aantal { font-size:.68rem; font-weight:700; opacity:.75; }
    .dlchip.aan { background:#c2410c; border-color:#c2410c; color:#fff; }
    .dlchip:disabled { opacity:.45; cursor:default; }
    /* In de selecteerstand is de aanwijzer een kruisdraad: dat zegt dat slepen hier een kader
       trekt en niet de kaart verschuift. */
    .kaart.selecteren, .kaart.selecteren .leaflet-grab, .kaart.selecteren .leaflet-container { cursor:crosshair; }
    .kaartmodus { position:absolute; right:10px; top:10px; z-index:1000; display:flex;
                  border-radius:7px; overflow:hidden; border:1px solid #d3d9d3;
                  box-shadow:0 1px 3px rgba(16,28,18,.18); }
    .kaartmodus button { display:flex; align-items:center; justify-content:center; width:30px; height:30px;
                         padding:0; border:none; background:#fff; color:var(--mv-text-sub); cursor:pointer; }
    .kaartmodus button + button { border-left:1px solid #d3d9d3; }
    .kaartmodus button:hover { color:var(--mv-primary); }
    .kaartmodus button.aan { background:var(--mv-primary); color:#fff; }
    .kaartmodus button:disabled { opacity:.45; cursor:default; }
    .kaartmodus button.dl:hover { color:#c2410c; }
    .kaartmodus button.dl.aan { background:#c2410c; color:#fff; }
    ${unsafeCSS(SUBLOCATION_MAP_CSS)}
    /* Uitleg en naamveld tijdens het tekenen: bovenaan in de kaart, links van het gereedschap. */
    .dlbalk { position:absolute; left:56px; right:120px; top:10px; z-index:1000; display:flex; align-items:center;
              flex-wrap:wrap; gap:8px; padding:7px 10px; border:1px solid #c2410c; border-radius:8px;
              background:#fff; box-shadow:0 2px 8px rgba(0,0,0,.18); font-size:.78rem; color:var(--mv-text); }
    .dlbalk button { border:none; background:var(--mv-bg,#f1f3f5); color:var(--mv-text); border-radius:6px;
                     padding:4px 10px; font:inherit; font-size:.74rem; font-weight:700; cursor:pointer; }
    .dlbalk button.hoofd { background:#c2410c; color:#fff; }
    .dlbalk button:disabled { opacity:.5; cursor:default; }
    .dlbalk input { border:1px solid var(--mv-border); border-radius:6px; padding:4px 8px; font:inherit; width:8rem; }
    .dlfout { flex-basis:100%; color:var(--mv-danger,#c0392b); font-size:.72rem; }
    ${unsafeCSS(MARKER_CSS)}
    /* De kaart staat in een apart venster: hier alleen een plek die dat zegt. */
    .vensterplek { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:14px 16px;
                   border:1.5px dashed var(--mv-border); border-radius:10px; font-size:.78rem; color:var(--mv-text-sub); }
    .vensterplek .tekstknop { font-size:.78rem; }

    .onderregel { display:flex; align-items:center; gap:8px; flex-wrap:wrap; margin-top:10px; font-size:.74rem; }
    .onderregel .stip { width:8px; height:8px; flex:none; border-radius:50%; background:var(--mv-border); }
    .onderregel .stip.aan { background:var(--mv-primary); }
    .selectieregel { font-weight:700; color:var(--mv-text); min-width:0; overflow:hidden; text-overflow:ellipsis; }

    /* 'Potjes gaan naar': het mandje als een regel knoppen. Het actieve mengmonster is groen;
       een rood stipje zegt dat het een mengregel breekt. */
    .naarbalk { display:flex; align-items:center; gap:6px; flex-wrap:wrap; margin-bottom:12px;
                padding:8px 10px; border-radius:9px; background:var(--mv-surface-alt); }
    .naarlabel { font-size:.72rem; font-weight:700; color:var(--mv-text-sub); margin-right:2px; }
    .naarchip { display:inline-flex; align-items:center; gap:5px; padding:4px 9px; border-radius:7px;
      border:1.5px solid var(--mv-border); background:var(--mv-surface); color:var(--mv-text);
      font:inherit; font-size:.74rem; font-weight:800; cursor:pointer; }
    .naarchip:hover:not(.aan) { border-color:var(--mv-primary); }
    .naarchip .aantal { font-size:.68rem; font-weight:700; color:var(--mv-text-muted); }
    .naarchip.aan { background:var(--mv-primary); border-color:#2f6122; color:#fff; }
    .naarchip.aan .aantal { color:#dcebd6; }
    .naamveld { width:170px; box-sizing:border-box; padding:4px 8px; border:1.5px solid var(--mv-primary);
                border-radius:7px; background:var(--mv-surface); color:var(--mv-text);
                font:inherit; font-size:.74rem; font-weight:800; outline:none; }
    .naamfout { margin:-6px 0 12px; font-size:.72rem; font-weight:700; color:var(--mv-danger); }
    .breekt { width:6px; height:6px; border-radius:50%; background:var(--mv-danger); }
    .naarchip.aan .breekt { background:#ffd2cc; }
    .mmwaarsch { margin:-4px 0 12px; padding:6px 10px 6px 26px; border-radius:8px; background:var(--mv-danger-bg);
                 color:var(--mv-danger); font-size:.7rem; font-weight:700; line-height:1.45; }
    /* Kaart boven, profielen en mengmonsters eronder -- ook op een breed scherm. Ze stonden
       daar een tijd naast elkaar (vanaf 1400px), maar dan schoven de mengmonsters rechts van de
       kaart en zocht je ze. De kaart op een tweede scherm kan nog altijd met het kaartvenster. */
    @media (max-width: 1100px) {
      .kaart { height:var(--kaart-hoogte, min(420px, 55vh)); }
    }
    @media (max-width: 640px) {
      .filterblok { grid-template-columns:minmax(0,1fr); }
      .rijlabel { padding:10px 0 0; }
      .rijlabel + .rij { border-top:none !important; padding-top:6px; }
      .kop, .kaartkolom, .profielkolom { padding-left:16px; padding-right:16px; }
      .filterblok { padding:2px 16px; }
    }
  `;
}

customElements.define('lab-mengmonsters', LabMengmonsters);
